import { toastError } from '@/lib/toast-error';
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/SharedAuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import { trpc, trpcClient } from "@/lib/trpc";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { dealContextSchema, type DealContextForm } from "./pricing-types";
import { Menu, Lock, Plus, FileText, Trash2, Search, ChevronDown, Check, Save, Shield } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Trans, useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  recalculatePricing,
  pmlSuggestedTeam as calcPmlSuggestedTeam,
  applyAllocationsToRows,
  normalizePmlSourcing,
  type OtherCostRow, type AssetCostEntry, type RoleCostEntry, type FinancingCostEntry, type CampaignTypeMediaEntry,
  type ContentLabelValue, type ContentAssetEquivEntry, type ContentSsrLookupEntry, type ContentCalcConstants, type ContentAllocations,
  type DesiredMarginEntry, type DesiredPricingEntry, type TeamRow, type AssetRow,
  type PMLCampaignRow, type PMLSourcingRow, type PMLSuggestedTeam, type ContentAssetRow, type ContentTeamRow, type ContentVariables,
  type CreatorAdvocateDetailRow, type ContentCreatorAdvocateDetailData,
} from "@/../../shared/pricing-calc";

export interface SheetData {
  clients: Array<{ name: string; segment: string }>;
  currencies: string[];
  societies: string[];
  revenueTypes: string[];
  dealTypes: string[];
  solutions: string[];
  paymentTerms: string[];
  roles: string[];
  countries: string[];
  assets: string[];
  seniorities: string[];
  assetCostLookup: AssetCostEntry[];
  roleCostLookup: RoleCostEntry[];
  financingCostLookup: FinancingCostEntry[];
  // PML-specific data
  campaignTypes: string[];
  complexities: string[];
  campaignTypeMediaLookup: CampaignTypeMediaEntry[];
  // Content-specific data
  contentClientGeos: string[];
  contentClientComplexity: string[];
  contentCreatorAdvocateComplexity: string[];
  contentAssetTypes: string[];
  contentPartnerCountries: string[];
  contentSpecialistCountries: string[];
  contentCreatorAdvocateCountries: string[];
  contentCreatorAdvocateLookup: Array<{ country: string; complexity: string; unitPrice: number; roleType: string }>;
  contentCpAssetLookup: ContentAssetEquivEntry[];
  contentCsAssetLookup: ContentAssetEquivEntry[];
  contentCpGeosLookup: ContentLabelValue[];
  contentCpComplexityLookup: ContentLabelValue[];
  contentCpClientComplexityLookup: ContentLabelValue[];
  contentCsGeosLookup: ContentLabelValue[];
  contentCsComplexityLookup: ContentLabelValue[];
  contentCpSsrLookup: ContentSsrLookupEntry[];
  contentCsSsrLookup: ContentSsrLookupEntry[];
  contentCalcConstants: ContentCalcConstants;
  // PML lookup data
  campaignTypeValueLookup: Array<{ campaignType: string; value: number }>;
  complexityClientLookup: Array<{ key: string; value: number }>;
  mediaValueLookup: Array<{ key: string; value: number }>;
  kpi2MediaValueLookup: Array<{ key: string; value: number }>;
  kpi2ComplexityLookup: Array<{ key: string; value: number }>;
  suggestedTeamConstants: { bh4: number; bi4: number; bi7: number; bi8: number; bi9: number; bi10: number };
}

// Helper to format numbers with commas
const formatNumberWithCommas = (num: number): string => {
  return Math.round(num).toLocaleString('en-US');
};

type TabType = "deal-context" | "team-assets" | "team-assets-pml" | "team-assets-content" | "margins";

const CONTENT_ROLES = [
  "Content Partner",
  "Content Partner Lead",
  "Content Specialist Sr",
  "Content Specialist Lead",
] as const;

/**
 * Editor del Pricing Manager — port del pricing.tsx de Replit (monolito).
 * Los CÁLCULOS viven en shared/pricing-calc.ts (funciones puras, snapshot
 * test en backend/src/lib/pricing-calc.test.ts capturado con las closures
 * originales): acá solo se leen del estado y se vuelcan al estado. Adaptaciones: wouter→react-router,
 * apiRequest→tRPC, use-toast→sonner, flags string→boolean, auth de la
 * plataforma. El header propio de Replit murió (lo da la Layout).
 */
export default function PricingEditor() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("deal-context");
  const [isMetricsOpen, setIsMetricsOpen] = useState(true);
  const [showSuperAdmin, setShowSuperAdmin] = useState(false);
  const [isDealContextSaved, setIsDealContextSaved] = useState(false);
  const [selectedSolutions, setSelectedSolutions] = useState<string[]>([]);
  const [versionId, setVersionId] = useState<string | null>(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showHubSpotReminder, setShowHubSpotReminder] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [savedVersionInfo, setSavedVersionInfo] = useState<{client: string; date: string; time: string; versionName: string} | null>(null);
  const [isVersionLocked, setIsVersionLocked] = useState(false);
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showPreApprovedDialog, setShowPreApprovedDialog] = useState(false);
  const [contentCreatorAdvocateCostRaw, setContentCreatorAdvocateCostRaw] = useState<number>(0);
  const [metricsSelectionChoice, setMetricsSelectionChoice] = useState<"desiredMargins" | "desiredPricing">("desiredMargins");
  const justLoadedVersion = useRef(false);
  const autoRecalcOnLoad = useRef(false);

  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Team & Assets state
  const [teamRows, setTeamRows] = useState<TeamRow[]>([]);
  const [assetRows, setAssetRows] = useState<AssetRow[]>([]);
  const [otherCostRows, setOtherCostRows] = useState<OtherCostRow[]>([]);
  
  // PML Campaign state
  const [pmlCampaignRows, setPmlCampaignRows] = useState<PMLCampaignRow[]>([]);
  const [pmlSourcingRows, setPmlSourcingRows] = useState<PMLSourcingRow[]>([{ id: generateId(), country: "", role: "", allocation: 0 }]);
  const [pmlComplexity, setPmlComplexity] = useState<string>("");
  const [pmlSuggestedTeam, setPmlSuggestedTeam] = useState<PMLSuggestedTeam>({
    srAnalystAllocation: null,
    leadAllocation: null,
  });
  
  // Content state
  const [contentVariables, setContentVariables] = useState<ContentVariables>({
    clientGeos: "",
    clientComplexity: "",
  });
  const [contentCreatorAdvocateDetailData, setContentCreatorAdvocateDetailData] = useState<ContentCreatorAdvocateDetailData>({
    creators: [],
    advocates: [],
  });
  const [contentAssetRows, setContentAssetRows] = useState<ContentAssetRow[]>([
    { id: generateId(), assetType: "", assetQuantity: 0 },
    { id: generateId(), assetType: "", assetQuantity: 0 },
    { id: generateId(), assetType: "", assetQuantity: 0 },
  ]);
  const [contentTeamRows, setContentTeamRows] = useState<ContentTeamRow[]>(
    CONTENT_ROLES.map(role => ({ role, country: "", allocationOrPrice: null }))
  );

  // Metrics panel state
  const [desiredMargins, setDesiredMargins] = useState<DesiredMarginEntry[]>([]);
  const [desiredPricings, setDesiredPricings] = useState<DesiredPricingEntry[]>([]);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  // Per-solution computed costs (populated by Recalculate, used for Bundle Pricing derivation)
  const [solutionCosts, setSolutionCosts] = useState<Record<string, number>>({});

  // Bundle Pricing results — set by Recalculate, cleared on any pending change
  const [bundleSuggestedTotal, setBundleSuggestedTotal] = useState<number | null>(null);
  const [bundleMarginFromSuggested, setBundleMarginFromSuggested] = useState<number | null>(null);
  const [bundleDesiredTotal, setBundleDesiredTotal] = useState<number | null>(null);
  const [bundleObtainedFromDesired, setBundleObtainedFromDesired] = useState<number | null>(null);

  // Fetch dropdown data from Google Sheets
  const { data: sheetData, isLoading: isLoadingData, isError: isDataError } =
    trpc.pricing.getSheetData.useQuery(undefined, { staleTime: 5 * 60 * 1000 });

  const form = useForm<DealContextForm>({
    resolver: zodResolver(dealContextSchema),
    defaultValues: {
      client: "",
      segment: "",
      currency: "",
      society: "",
      revenueType: "",
      dealType: "",
      solutions: [],
      durationMonths: undefined as unknown as number,
      paymentTerms: "",
      versionName: "",
      comments: "",
    },
  });

  // Handle viewing a saved version by ID (read-only)
  const routeParams = useParams<{ id: string }>();
  const viewVersionId = routeParams.id && routeParams.id !== "new" ? routeParams.id : null;
  const [viewVersionLoaded, setViewVersionLoaded] = useState(false);
  // Store raw fetched version data; form.reset() is deferred until sheetData is ready
  const [pendingViewVersion, setPendingViewVersion] = useState<any>(null);
  const [pendingDuplicateVersion, setPendingDuplicateVersion] = useState<any>(null);

  // Shared helper: apply all version state from raw data
  const applyVersionToState = (version: any, isDuplicate: boolean) => {
    form.reset({
      client: version.client || "",
      segment: version.segment || "",
      currency: version.currency || "",
      society: version.society || "",
      revenueType: version.revenueType || "",
      dealType: version.dealType || "",
      solutions: version.solutions || [],
      durationMonths: version.durationMonths || 1,
      paymentTerms: version.paymentTerms || "",
      versionName: isDuplicate ? "" : (version.versionName || ""),
      comments: version.comments || "",
    });
    setSelectedSolutions(version.solutions || []);
    if (version.teamData && Array.isArray(version.teamData)) setTeamRows(version.teamData);
    if (version.assetData && Array.isArray(version.assetData)) setAssetRows(version.assetData);
    if (version.otherCostsData && Array.isArray(version.otherCostsData)) setOtherCostRows(version.otherCostsData);
    if (version.pmlCampaignData && Array.isArray(version.pmlCampaignData)) setPmlCampaignRows(version.pmlCampaignData);
    if (version.pmlSourcingTeamData) {
      // Array (editor del BO) u objeto viejo de Replit: una sola lectura, la misma que usa el reporte.
      const rows = normalizePmlSourcing(version.pmlSourcingTeamData, () => generateId());
      if (Array.isArray(version.pmlSourcingTeamData) || rows.length > 0) setPmlSourcingRows(rows);
    }
    if (version.pmlComplexity) setPmlComplexity(version.pmlComplexity);
    if (version.contentVariablesData) setContentVariables(version.contentVariablesData);
    if (version.contentAssetData && Array.isArray(version.contentAssetData)) setContentAssetRows(version.contentAssetData);
    if (version.contentTeamData && Array.isArray(version.contentTeamData)) {
      setContentTeamRows(version.contentTeamData.filter((r: any) => r.role !== "Creators" && r.role !== "Advocates"));
    }
    if (version.contentCreatorAdvocateDetailData) setContentCreatorAdvocateDetailData(version.contentCreatorAdvocateDetailData);
    if (version.metricsData) {
      if (version.metricsData.desiredMargins) setDesiredMargins(version.metricsData.desiredMargins);
      if (version.metricsData.desiredPricings) setDesiredPricings(version.metricsData.desiredPricings);
      if (version.metricsData.solutionCosts) setSolutionCosts(version.metricsData.solutionCosts);
      if (version.metricsData.contentCreatorAdvocateCostRaw != null) setContentCreatorAdvocateCostRaw(version.metricsData.contentCreatorAdvocateCostRaw);
      if (version.metricsData.bundleSuggestedTotal != null) setBundleSuggestedTotal(version.metricsData.bundleSuggestedTotal);
      if (version.metricsData.bundleMarginFromSuggested != null) setBundleMarginFromSuggested(version.metricsData.bundleMarginFromSuggested);
      if (version.metricsData.bundleDesiredTotal != null) setBundleDesiredTotal(version.metricsData.bundleDesiredTotal);
      if (version.metricsData.bundleObtainedFromDesired != null) setBundleObtainedFromDesired(version.metricsData.bundleObtainedFromDesired);
    }
  };

  // Step 1: fetch the version, store raw data only (no form.reset yet)
  useEffect(() => {
    if (viewVersionLoaded || !viewVersionId) return;
    trpcClient.pricing.getVersion.query({ id: viewVersionId })
      .then((version: any) => {
        if (!version) { setViewVersionLoaded(true); return; }
        setPendingViewVersion(version);
      })
      .catch(() => setViewVersionLoaded(true));
  }, [viewVersionLoaded, viewVersionId]);

  // Step 2: apply once BOTH the version data AND sheetData (Select options) are ready
  useEffect(() => {
    if (!pendingViewVersion || !sheetData) return;
    const version = pendingViewVersion;
    setPendingViewVersion(null);

    applyVersionToState(version, false);

    setVersionId(version.versionId);
    setIsDealContextSaved(true);
    const locked = version.isLocked === true;
    setIsVersionLocked(locked);
    if (!locked) {
      justLoadedVersion.current = true;
      setTimeout(() => { justLoadedVersion.current = false; }, 300);
    }
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
    const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }).replace(':', '-');
    setSavedVersionInfo({ client: version.client || "", date: dateStr, time: timeStr, versionName: version.versionName || "" });
    setViewVersionLoaded(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingViewVersion, sheetData]);

  // Handle duplicate parameter - load version data
  const [duplicateLoaded, setDuplicateLoaded] = useState(false);

  // Step 1: fetch duplicate, store raw data only
  useEffect(() => {
    if (duplicateLoaded) return;
    const params = new URLSearchParams(window.location.search);
    const duplicateId = params.get('duplicate');
    if (!duplicateId) return;
    trpcClient.pricing.getVersion.query({ id: duplicateId })
      .then((version: any) => {
        if (!version) { setDuplicateLoaded(true); return; }
        setPendingDuplicateVersion(version);
      })
      .catch(() => setDuplicateLoaded(true));
  }, [duplicateLoaded]);

  // Step 2: apply duplicate once sheetData is ready
  useEffect(() => {
    if (!pendingDuplicateVersion || !sheetData) return;
    const version = pendingDuplicateVersion;
    setPendingDuplicateVersion(null);
    applyVersionToState(version, true);
    setDuplicateLoaded(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDuplicateVersion, sheetData]);

  // Watch client to auto-populate segment
  const selectedClient = form.watch("client");

  useEffect(() => {
    if (!selectedClient || !sheetData?.clients) return;
    if (selectedClient === "Other New Client") {
      form.setValue("segment", "");
      return;
    }
    const clientData = sheetData.clients.find(c => c.name === selectedClient);
    if (clientData?.segment) {
      form.setValue("segment", clientData.segment);
    }
  }, [selectedClient, sheetData?.clients, form]);

  // Update solutions in form when selectedSolutions changes
  useEffect(() => {
    form.setValue("solutions", selectedSolutions);
  }, [selectedSolutions, form]);

  // Get duration from form for Team & Assets
  const durationMonths = form.watch("durationMonths") || 1;

  // Filter solutions for Team & Assets (exclude Content and Paid Media Lab)
  const teamAssetsSolutions = selectedSolutions.filter(
    s => s !== "Content" && s !== "Paid Media Lab"
  );

  // Check if Paid Media Lab is selected
  const isPmlSelected = selectedSolutions.includes("Paid Media Lab");
  const isContentSelected = selectedSolutions.includes("Content");

  // Get unique countries for PML dropdowns
  const uniqueCountries = sheetData?.roleCostLookup 
    ? Array.from(new Set(sheetData.roleCostLookup.map(entry => entry.country))).sort()
    : [];

  // Team row handlers
  const clearMetricsOnChange = () => {
    setHasPendingChanges(true);
    // Only clear metrics for regular (non-PML, non-Content) solutions — those tabs manage their own state
    setDesiredMargins(prev => prev.map(m => {
      if (m.solution === "Paid Media Lab" || m.solution === "Content") return m;
      return { ...m, suggestedPricing: null };
    }));
    setDesiredPricings(prev => prev.map(p => {
      if (p.solution === "Paid Media Lab" || p.solution === "Content") return p;
      return { ...p, obtainedMargin: null };
    }));
  };

  const addTeamRow = () => {
    const newRow: TeamRow = {
      id: generateId(),
      role: "",
      country: "",
      group: "",
      allocation: 0,
    };
    setTeamRows([...teamRows, newRow]);
    clearMetricsOnChange();
  };

  const updateTeamRow = (id: string, field: keyof TeamRow, value: any) => {
    setTeamRows(rows => rows.map(row => {
      if (row.id === id) {
        return { ...row, [field]: value };
      }
      return row;
    }));
    clearMetricsOnChange();
  };

  const deleteTeamRow = (id: string) => {
    setTeamRows(rows => rows.filter(row => row.id !== id));
    clearMetricsOnChange();
  };

  const addAssetRow = () => {
    const newRow: AssetRow = {
      id: generateId(),
      asset: "",
      seniority: "",
      unitCost: null,
      group: "Owned Media Lab",
      totalQuantity: 0,
    };
    setAssetRows([...assetRows, newRow]);
    clearMetricsOnChange();
  };

  const updateAssetRow = (id: string, field: keyof AssetRow, value: any) => {
    setAssetRows(rows => rows.map(row => {
      if (row.id === id) {
        const updatedRow = { ...row, [field]: value };
        if ((field === 'asset' || field === 'seniority') && sheetData?.assetCostLookup) {
          const asset = field === 'asset' ? value : row.asset;
          const seniority = field === 'seniority' ? value : row.seniority;
          if (asset && seniority) {
            const match = sheetData.assetCostLookup.find(
              entry => entry.asset === asset && entry.seniority === seniority
            );
            updatedRow.unitCost = match ? match.unitCost : null;
          }
        }
        return updatedRow;
      }
      return row;
    }));
    clearMetricsOnChange();
  };

  const deleteAssetRow = (id: string) => {
    setAssetRows(rows => rows.filter(row => row.id !== id));
    clearMetricsOnChange();
  };

  // Other Costs row handlers
  const addOtherCostRow = () => {
    const defaultSolution = teamAssetsSolutions[0] || "";
    setOtherCostRows(rows => [...rows, { id: generateId(), description: "", solution: defaultSolution, monthlyCostUSD: 0 }]);
    clearMetricsOnChange();
  };

  const addPmlOtherCostRow = () => {
    setOtherCostRows(rows => [...rows, { id: generateId(), description: "", solution: "Paid Media Lab", monthlyCostUSD: 0 }]);
    clearMetricsOnChange();
  };

  const updateOtherCostRow = (id: string, field: keyof OtherCostRow, value: string | number) => {
    setOtherCostRows(rows => rows.map(row => row.id === id ? { ...row, [field]: value } : row));
    clearMetricsOnChange();
  };

  const deleteOtherCostRow = (id: string) => {
    setOtherCostRows(rows => rows.filter(row => row.id !== id));
    clearMetricsOnChange();
  };

  const createEmptyPmlRow = (): PMLCampaignRow => ({
    id: generateId(),
    campaignName: "",
    campaignType: "",
    campaignMedia: "",
    campaignsQuantity: 0,
  });

  // Initialize PML with one row when PML is selected and no rows exist
  useEffect(() => {
    if (isPmlSelected && pmlCampaignRows.length === 0) {
      setPmlCampaignRows([createEmptyPmlRow()]);
    }
  }, [isPmlSelected]);

  const clearPmlSuggestedTeam = () => {
    setPmlSuggestedTeam({
      srAnalystAllocation: null,
      leadAllocation: null,
    });
  };

  // Real-time suggested team: recalculate whenever campaign rows, complexity, or sheet data changes
  useEffect(() => {
    if (isVersionLocked) return;
    const allRowsComplete =
      pmlCampaignRows.length > 0 &&
      pmlCampaignRows.every(
        row => row.campaignName.trim() !== "" && row.campaignType !== "" && row.campaignsQuantity > 0
      );
    if (allRowsComplete && pmlComplexity && sheetData) {
      calculatePmlSuggestedTeam();
    } else {
      clearPmlSuggestedTeam();
    }
  }, [pmlCampaignRows, pmlComplexity, sheetData, isVersionLocked]);

  const updatePmlCampaignRow = (id: string, field: keyof PMLCampaignRow, value: any) => {
    setPmlCampaignRows(rows => rows.map(row => {
      if (row.id === id) {
        const updatedRow = { ...row, [field]: value };
        
        if (field === 'campaignType' && sheetData?.campaignTypeMediaLookup) {
          const mediaEntry = sheetData.campaignTypeMediaLookup.find(
            entry => entry.campaignType === value
          );
          updatedRow.campaignMedia = mediaEntry?.campaignMedia || "";
        }
        
        return updatedRow;
      }
      return row;
    }));
    setHasPendingChanges(true);
    clearPmlMetrics();
  };

  const addPmlSourcingRow = () => {
    setPmlSourcingRows(prev => [...prev, { id: generateId(), country: "", role: "", allocation: 0 }]);
    setHasPendingChanges(true);
    clearPmlMetrics();
  };

  const updatePmlSourcingRow = (id: string, field: keyof PMLSourcingRow, value: any) => {
    setPmlSourcingRows(prev => prev.map(row => row.id === id ? { ...row, [field]: value } : row));
    setHasPendingChanges(true);
    clearPmlMetrics();
  };

  const deletePmlSourcingRow = (id: string) => {
    setPmlSourcingRows(prev => prev.length <= 1 ? [{ id: generateId(), country: "", role: "", allocation: 0 }] : prev.filter(r => r.id !== id));
    setHasPendingChanges(true);
    clearPmlMetrics();
  };

  // Clear PML metrics when data changes
  const clearPmlMetrics = () => {
    setDesiredMargins(prev => prev.map(m => 
      m.solution === "Paid Media Lab" ? { ...m, suggestedPricing: null } : m
    ));
    setDesiredPricings(prev => prev.map(p => 
      p.solution === "Paid Media Lab" ? { ...p, obtainedMargin: null } : p
    ));
  };

  const addPmlCampaignRow = () => {
    setPmlCampaignRows(rows => [...rows, createEmptyPmlRow()]);
    setHasPendingChanges(true);
    clearPmlMetrics();
  };

  const deletePmlCampaignRow = (id: string) => {
    if (pmlCampaignRows.length <= 1) {
      setPmlCampaignRows([createEmptyPmlRow()]);
    } else {
      setPmlCampaignRows(rows => rows.filter(row => row.id !== id));
    }
    setHasPendingChanges(true);
    clearPmlMetrics();
  };

  // Content helpers
  const getContentCountriesForRole = (role: string): string[] => {
    if (!sheetData) return [];
    if (role === "Content Partner" || role === "Content Partner Lead") {
      return sheetData.contentPartnerCountries || [];
    }
    if (role === "Content Specialist Sr" || role === "Content Specialist Lead") {
      return sheetData.contentSpecialistCountries || [];
    }
    if (role === "Creators" || role === "Advocates") {
      return sheetData.contentCreatorAdvocateCountries || [];
    }
    return [];
  };

  const updateContentTeamCountry = (index: number, country: string) => {
    setContentTeamRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], country, allocationOrPrice: null };
      return updated;
    });
    setHasPendingChanges(true);
  };

  const addContentAssetRow = () => {
    setContentAssetRows(prev => [...prev, { id: generateId(), assetType: "", assetQuantity: 0 }]);
  };

  const deleteContentAssetRow = (id: string) => {
    if (contentAssetRows.length <= 1) {
      setContentAssetRows([{ id: generateId(), assetType: "", assetQuantity: 0 }]);
    } else {
      setContentAssetRows(prev => prev.filter(row => row.id !== id));
    }
  };

  // Helper function to check if a solution has any team or asset data
  const solutionHasData = (solution: string): boolean => {
    if (solution === "Paid Media Lab") {
      const hasSourcingData = pmlSourcingRows.some(r => r.allocation > 0);
      const hasCampaignData = pmlCampaignRows.some(
        row => row.campaignType !== "" || row.campaignsQuantity > 0
      );
      return hasSourcingData || hasCampaignData;
    }
    if (solution === "Content") {
      const hasComputedAlloc = contentTeamRows.some(
        r => r.allocationOrPrice !== null && r.allocationOrPrice !== undefined
      );
      const hasAsset = contentAssetRows.some(r => r.assetType && r.assetQuantity >= 1);
      return hasComputedAlloc || hasAsset;
    }
    const hasTeamData = teamRows.some(row => row.group === solution);
    const hasAssetData = assetRows.some(row => row.group === solution);
    const hasOtherCosts = otherCostRows.some(row => row.solution === solution);
    return hasTeamData || hasAssetData || hasOtherCosts;
  };

  // Helper function to get available countries for a given role
  const getCountriesForRole = (role: string): string[] => {
    if (!role || !sheetData?.roleCostLookup) return sheetData?.countries || [];
    const countries = sheetData.roleCostLookup
      .filter(entry => entry.role === role)
      .map(entry => entry.country);
    const uniqueCountries = Array.from(new Set(countries));
    return uniqueCountries.length > 0 ? uniqueCountries : sheetData?.countries || [];
  };

  // Helper function to get available roles for a given country
  const getRolesForCountry = (country: string): string[] => {
    if (!country || !sheetData?.roleCostLookup) return sheetData?.roles || [];
    const roles = sheetData.roleCostLookup
      .filter(entry => entry.country === country)
      .map(entry => entry.role);
    const uniqueRoles = Array.from(new Set(roles));
    return uniqueRoles.length > 0 ? uniqueRoles : sheetData?.roles || [];
  };

  const calculatePmlSuggestedTeam = () => {
    const suggested = calcPmlSuggestedTeam({ sheet: sheetData, pmlCampaignRows, pmlComplexity });
    if (suggested) setPmlSuggestedTeam(suggested);
    else clearPmlSuggestedTeam();
  };

  // Apply computed Content allocations to contentTeamRows
  const applyContentAllocations = (allocs: ContentAllocations) => {
    setContentTeamRows(prev => applyAllocationsToRows(prev, allocs));
  };

  // Calculate suggested pricing based on costs and desired margin
  const handleRecalculate = () => {
    if (!sheetData?.roleCostLookup) return;

    // Todo el cálculo vive en shared/pricing-calc (puro, con snapshot test); acá solo se vuelca al estado.
    const r = recalculatePricing({
      sheet: sheetData,
      durationMonths,
      paymentTerms: form.getValues("paymentTerms"),
      teamAssetsSolutions,
      isPmlSelected,
      isContentSelected,
      teamRows,
      assetRows,
      otherCostRows,
      pmlSourcingRows,
      pmlCampaignRows,
      pmlComplexity,
      contentVariables,
      contentAssetRows,
      contentTeamRows,
      contentCreatorAdvocateDetailData,
      desiredMargins,
      desiredPricings,
    });
    const freshContentAllocs = r.contentAllocations ?? null;
    if (r.contentCosts) setContentCreatorAdvocateCostRaw(r.contentCosts.rawCreatorsCost);

    setDesiredMargins(r.metrics.margins);
    setDesiredPricings(r.metrics.pricings);

    // Store per-solution costs so Bundle Pricing can derive aggregates
    setSolutionCosts(r.costs);

    // Bundle Pricing results (null salvo con más de una solución)
    setBundleSuggestedTotal(r.metrics.bundle.suggestedTotal);
    setBundleMarginFromSuggested(r.metrics.bundle.marginFromSuggested);
    setBundleDesiredTotal(r.metrics.bundle.desiredTotal);
    setBundleObtainedFromDesired(r.metrics.bundle.obtainedFromDesired);

    setHasPendingChanges(false);

    if (r.pmlSuggestedTeam !== undefined) {
      if (r.pmlSuggestedTeam) setPmlSuggestedTeam(r.pmlSuggestedTeam);
      else clearPmlSuggestedTeam();
    }

    // Apply Content team allocations after PML (state update, async)
    if (isContentSelected && freshContentAllocs) {
      applyContentAllocations(freshContentAllocs);
    }

    toast.success(t('pricing.editor.metrics_updated'), {
      description: t('pricing.editor.pricing_calculations_have_been_updated_based'),
    });
  };

  // Clear metrics for solutions that no longer have team/asset/other cost data
  // NOTE: PML and Content are excluded — they use separate tabs (pmlCampaignRows,
  // contentTeamRows, etc.) and must not be reset when regular teamRows change.
  useEffect(() => {
    if (isVersionLocked) return;
    // Reset margins for solutions without data (skip PML and Content — own tabs)
    setDesiredMargins(prev => prev.map(m => {
      if (m.solution === "Paid Media Lab" || m.solution === "Content") return m;
      const hasData = teamRows.some(row => row.group === m.solution) || assetRows.some(row => row.group === m.solution) || otherCostRows.some(row => row.solution === m.solution);
      if (!hasData) {
        return { ...m, margin: 0, suggestedPricing: null };
      }
      return m;
    }));
    
    // Reset pricing for solutions without data (skip PML and Content — own tabs)
    setDesiredPricings(prev => prev.map(p => {
      if (p.solution === "Paid Media Lab" || p.solution === "Content") return p;
      const hasData = teamRows.some(row => row.group === p.solution) || assetRows.some(row => row.group === p.solution) || otherCostRows.some(row => row.solution === p.solution);
      if (!hasData) {
        return { ...p, desiredPricing: null, obtainedMargin: null };
      }
      return p;
    }));
  }, [teamRows, assetRows, otherCostRows, isVersionLocked]);

  // Clear bundle results whenever any change is pending (same lifecycle as individual solution metrics)
  useEffect(() => {
    if (hasPendingChanges && !isVersionLocked) {
      setBundleSuggestedTotal(null);
      setBundleMarginFromSuggested(null);
      setBundleDesiredTotal(null);
      setBundleObtainedFromDesired(null);
    }
  }, [hasPendingChanges, isVersionLocked]);

  // Auto-recalculate once when a saved version is fully loaded AND sheet data is ready
  useEffect(() => {
    if (!viewVersionId || !viewVersionLoaded || !sheetData || autoRecalcOnLoad.current) return;
    autoRecalcOnLoad.current = true;
    handleRecalculate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewVersionLoaded, sheetData, viewVersionId]);

  // Mark unsaved edits whenever pending changes appear or metrics inputs change
  useEffect(() => {
    if (isVersionLocked || justLoadedVersion.current) return;
    if (hasPendingChanges) setHasUnsavedEdits(true);
  }, [hasPendingChanges, isVersionLocked]);

  useEffect(() => {
    if (isVersionLocked || justLoadedVersion.current) return;
    if (isDealContextSaved) setHasUnsavedEdits(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desiredMargins, desiredPricings]);

  // Save version mutation
  const saveVersionMutation = useMutation({
    mutationFn: async (data: DealContextForm) => trpcClient.pricing.createVersion.mutate(data),
    onSuccess: (data: any) => {
      const now = new Date();
      const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
      const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }).replace(':', '-');
      
      setSavedVersionInfo({
        client: form.getValues("client"),
        date: dateStr,
        time: timeStr,
        versionName: form.getValues("versionName"),
      });
      const isFirstSave = !versionId;
      setVersionId(data.versionId);
      setIsDealContextSaved(true);
      if (isFirstSave) {
        navigate(`/finance/pricing/${data.versionId}`);
      }
      toast.success(t('pricing.editor.version_saved'), {
        description: t('pricing.editor.deal_context_has_been_saved_you'),
      });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Failed to save version",
      });
    },
  });

  const buildVersionPayload = () => {
    const formValues = form.getValues();
    return {
    isSaved: true,
    savedAt: new Date().toISOString(),
    savedByName: user?.email || 'Unknown',
    client: formValues.client,
    segment: formValues.segment,
    currency: formValues.currency,
    society: formValues.society,
    revenueType: formValues.revenueType,
    dealType: formValues.dealType,
    solutions: selectedSolutions,
    durationMonths: formValues.durationMonths,
    paymentTerms: formValues.paymentTerms,
    versionName: formValues.versionName,
    comments: formValues.comments,
    teamData: teamRows,
    assetData: assetRows,
    otherCostsData: otherCostRows,
    pmlCampaignData: pmlCampaignRows,
    pmlSourcingTeamData: pmlSourcingRows,
    pmlComplexity: pmlComplexity,
    contentVariablesData: contentVariables,
    contentAssetData: contentAssetRows,
    contentTeamData: contentTeamRows,
    contentCreatorAdvocateDetailData: contentCreatorAdvocateDetailData,
    metricsData: {
      desiredMargins,
      desiredPricings,
      pmlSuggestedTeam,
      solutionCosts,
      contentCreatorAdvocateCostRaw,
      bundleSuggestedTotal,
      bundleMarginFromSuggested,
      bundleDesiredTotal,
      bundleObtainedFromDesired,
    },
  };
  };

  const saveChangesMutation = useMutation({
    mutationFn: async () => {
      if (!versionId) throw new Error("No version to save");
      return trpcClient.pricing.save.mutate({
        id: versionId,
        ...buildVersionPayload(),
        isLocked: false,
        reportUrl: null,
      });
    },
    onSuccess: () => {
      setHasUnsavedEdits(false);
      toast.success(t('pricing.editor.changes_saved'), {
        description: t('pricing.editor.your_pricing_has_been_saved_you'),
      });
    },
    onError: (error) => {
      toastError(error, 'common.toast.failed_to_save_changes');
    },
  });

  const sendForApprovalMutation = useMutation({
    mutationFn: async (metricsSelection: "desiredMargins" | "desiredPricing") => {
      if (!versionId) throw new Error("No version to save");
      await trpcClient.pricing.save.mutate({ id: versionId, ...buildVersionPayload() });
      return trpcClient.pricing.sendForApproval.mutate({ id: versionId, metricsSelection });
    },
    onSuccess: (data: any) => {
      setIsVersionLocked(true);
      setShowApprovalDialog(false);
      const emailWarning = data.emailErrors?.length
        ? ` Note: Some emails failed to send: ${data.emailErrors.join(', ')}`
        : '';
      toast.success(t('pricing.editor.sent_for_approval'), {
        description: `This version is now locked and approval requests have been sent.${emailWarning}`,
      });
      if (data.isPreApproved) {
        setShowPreApprovedDialog(true);
      }
    },
    onError: (error) => {
      toastError(error, 'common.toast.failed_to_send_for_approval');
    },
  });

  // Handle navigation with save confirmation
  const handleNavigationRequest = (destination: string) => {
    if (form.formState.isDirty && !isDealContextSaved) {
      setPendingNavigation(destination);
      setShowSaveDialog(true);
    } else {
      // Use hard navigation to guarantee a full re-mount (SPA navigate is a no-op when URL matches)
      window.location.assign(destination);
    }
  };

  const handleSaveAndNavigate = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      saveVersionMutation.mutate(form.getValues(), {
        onSuccess: () => {
          setShowSaveDialog(false);
          if (pendingNavigation) {
            window.location.assign(pendingNavigation);
          }
          setPendingNavigation(null);
        },
      });
    } else {
      toast.error(t('pricing.editor.validation_error'), {
        description: t('pricing.editor.please_complete_all_required_fields_before'),
      });
      setShowSaveDialog(false);
      setPendingNavigation(null);
    }
  };

  const handleDiscardAndNavigate = () => {
    setShowSaveDialog(false);
    setPendingNavigation(null);
    if (pendingNavigation) {
      // Use hard navigation so the pricing component fully re-mounts with clean state
      // (SPA navigate won't remount when the destination equals the current URL)
      window.location.assign(pendingNavigation);
    }
  };

  const onSubmit = (data: DealContextForm) => {
    saveVersionMutation.mutate(data);
  };

  const handleTabClick = (tab: TabType) => {
    if (tab !== "deal-context" && !isDealContextSaved) {
      toast.error(t('pricing.editor.complete_deal_context_first'), {
        description: t('pricing.editor.please_save_the_deal_context_before'),
      });
      return;
    }
    setActiveTab(tab);
  };

  const watchedDealType = form.watch("dealType");
  const isSingleDeal = watchedDealType === "Single Deal";

  useEffect(() => {
    if (isSingleDeal && selectedSolutions.length > 1) {
      setSelectedSolutions([selectedSolutions[0]]);
    }
  }, [isSingleDeal]);

  const toggleSolution = (solution: string) => {
    setSelectedSolutions(prev => {
      if (prev.includes(solution)) {
        return prev.filter(s => s !== solution);
      }
      if (isSingleDeal) {
        return [solution];
      }
      return [...prev, solution];
    });
    setHasPendingChanges(true);
    setDesiredMargins(prev => prev.map(m => ({ ...m, suggestedPricing: null })));
    setDesiredPricings(prev => prev.map(p => ({ ...p, obtainedMargin: null })));
  };

  const getTabClassName = (tab: TabType) => {
    const isActive = activeTab === tab;
    const isLocked = tab !== "deal-context" && !isDealContextSaved;
    
    if (isActive) {
      return "flex-1 py-3 text-center font-medium transition-colors bg-primary text-primary-foreground";
    }
    if (isLocked) {
      return "flex-1 py-3 text-center font-medium transition-colors bg-muted text-muted-foreground cursor-not-allowed";
    }
    return "flex-1 py-3 text-center font-medium transition-colors bg-secondary text-secondary-foreground cursor-pointer hover-elevate";
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">

      {/* Version name bar with hamburger menu */}
      <div className="bg-muted border-b px-6 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={t('common.a11y.menu')} data-testid="button-hamburger-menu">
                <Menu className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => handleNavigationRequest("/finance/pricing/new")} data-testid="menu-new-pricing">
                <Plus className="w-4 h-4 mr-2" />
                {t('pricing.editor.new_pricing')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleNavigationRequest("/finance/pricing")} data-testid="menu-saved-versions">
                <FileText className="w-4 h-4 mr-2" />
                {t('pricing.editor.saved_versions')}
              </DropdownMenuItem>
              {user?.roles?.includes('admin') && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowSuperAdmin(true)} data-testid="menu-super-admin" className="text-yellow-500">
                    <Shield className="w-4 h-4 mr-2" />
                    {t('pricing.editor.super_admin')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="text-sm text-muted-foreground" data-testid="text-version-info">
            {savedVersionInfo ? (
              <span>{savedVersionInfo.client}_{savedVersionInfo.date}_{savedVersionInfo.time}_{savedVersionInfo.versionName}</span>
            ) : (
              <span>{t('pricing.editor.new_pricing')}</span>
            )}
          </div>
        </div>
        {isDealContextSaved && !isVersionLocked && (
          <div className="flex items-center gap-2">
            <Button
              onClick={() => saveChangesMutation.mutate()}
              disabled={saveChangesMutation.isPending || sendForApprovalMutation.isPending}
              className={`px-4 text-sm ${hasUnsavedEdits ? "bg-yellow-500 hover:bg-yellow-600 text-black" : "bg-gray-400 hover:bg-gray-500 text-white"}`}
              data-testid="button-save-changes"
            >
              <Save className="w-4 h-4 mr-2" />
              {saveChangesMutation.isPending ? t('pricing.editor.saving') : t('pricing.editor.save_changes')}
            </Button>
            <Button
              onClick={() => setShowApprovalDialog(true)}
              disabled={sendForApprovalMutation.isPending || saveChangesMutation.isPending}
              className="px-4 text-sm bg-yellow-500 hover:bg-yellow-600 text-black"
              data-testid="button-send-for-approval"
            >
              <Lock className="w-4 h-4 mr-2" />
              {sendForApprovalMutation.isPending ? t('pricing.editor.sending') : t('pricing.editor.send_for_approval')}
            </Button>
          </div>
        )}
        {isVersionLocked && (
          <Button
            disabled
            className="px-6 bg-gray-400 hover:bg-gray-400 cursor-default text-white"
            data-testid="button-save-version-locked"
          >
            <Lock className="w-4 h-4 mr-2" />
            Locked
          </Button>
        )}
      </div>

      {/* Save confirmation dialog */}
      <AlertDialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('pricing.editor.save_changes')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('pricing.editor.would_you_like_to_save_current')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDiscardAndNavigate}>{t('pricing.editor.don_t_save')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveAndNavigate}>Save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* HubSpot reminder dialog */}
      <AlertDialog open={showHubSpotReminder} onOpenChange={setShowHubSpotReminder}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('pricing.editor.other_new_client_selected')}</AlertDialogTitle>
            <AlertDialogDescription>
              <Trans i18nKey="pricing.editor.remember_to_upload_hubspot" components={{ b: <span className="font-bold text-foreground" /> }} />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowHubSpotReminder(false)}>
              {t('pricing.editor.got_it')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send for Approval dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('pricing.editor.send_for_approval')}</DialogTitle>
            <DialogDescription>
              {t('pricing.editor.you_are_sending_this_version_for')}
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const allSols: string[] = [...teamAssetsSolutions];
            if (isPmlSelected && !allSols.includes("Paid Media Lab")) allSols.push("Paid Media Lab");
            if (isContentSelected && !allSols.includes("Content")) allSols.push("Content");
            const missingSuggested = allSols.filter(sol => {
              const entry = desiredMargins.find(m => m.solution === sol);
              return !entry || entry.suggestedPricing == null;
            });
            const missingDesired = allSols.filter(sol => {
              const entry = desiredPricings.find(p => p.solution === sol);
              return !entry || !entry.desiredPricing;
            });
            const warnSols = metricsSelectionChoice === "desiredMargins" ? missingSuggested : missingDesired;
            if (warnSols.length === 0) return null;
            return (
              <div className="flex items-start gap-2 rounded-md border border-yellow-500/40 bg-yellow-500/10 px-3 py-2 text-sm text-yellow-400">
                <span className="mt-0.5 shrink-0">⚠</span>
                <span>
                  <Trans
                    i18nKey="pricing.editor.solutions_missing_metric"
                    count={warnSols.length}
                    values={{ metric: metricsSelectionChoice === "desiredMargins" ? t('pricing.editor.suggested_pricing') : t('pricing.editor.desired_pricing'), sols: warnSols.join(", ") }}
                    components={{ b: <span className="font-semibold" /> }}
                  />
                </span>
              </div>
            );
          })()}
          <div className="flex flex-col gap-3 py-4">
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="radio"
                name="metricsSelection"
                value="desiredMargins"
                checked={metricsSelectionChoice === "desiredMargins"}
                onChange={() => setMetricsSelectionChoice("desiredMargins")}
                className="accent-yellow-500 w-4 h-4"
                data-testid="radio-desired-margins"
              />
              <span className="text-sm font-medium">{t('pricing.editor.desired_margins')}</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="radio"
                name="metricsSelection"
                value="desiredPricing"
                checked={metricsSelectionChoice === "desiredPricing"}
                onChange={() => setMetricsSelectionChoice("desiredPricing")}
                className="accent-yellow-500 w-4 h-4"
                data-testid="radio-desired-pricing"
              />
              <span className="text-sm font-medium">{t('pricing.editor.desired_pricing')}</span>
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowApprovalDialog(false)}
              disabled={sendForApprovalMutation.isPending}
              data-testid="button-approval-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => sendForApprovalMutation.mutate(metricsSelectionChoice)}
              disabled={sendForApprovalMutation.isPending}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              data-testid="button-approval-send"
            >
              {sendForApprovalMutation.isPending ? t('pricing.editor.sending') : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pre-Approved Dialog */}
      <Dialog open={showPreApprovedDialog} onOpenChange={setShowPreApprovedDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-yellow-500 text-center text-xl">{t('pricing.editor.pre_approved')}</DialogTitle>
            <DialogDescription className="text-center pt-2 text-base">
              {t('pricing.editor.this_pricing_meets_all_pre_approval')} <strong>Pre-Approved</strong>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="justify-center">
            <Button
              onClick={() => setShowPreApprovedDialog(false)}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              data-testid="button-preapproved-ok"
            >
              {t('pricing.editor.got_it')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Super Admin Dialog */}
      <Dialog open={showSuperAdmin} onOpenChange={setShowSuperAdmin}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-500">
              <Shield className="w-5 h-5" />
              {t('pricing.editor.super_admin_sheet_data_sanity_check')}
            </DialogTitle>
            <DialogDescription>{t('pricing.editor.all_values_currently_loaded_from_the')}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            {sheetData ? (() => {
              const sections: Array<{ label: string; value: unknown }> = [
                { label: "Clients", value: sheetData.clients },
                { label: "Currencies", value: sheetData.currencies },
                { label: "Societies", value: sheetData.societies },
                { label: t('pricing.editor.revenue_types'), value: sheetData.revenueTypes },
                { label: t('pricing.editor.deal_types'), value: sheetData.dealTypes },
                { label: "Solutions", value: sheetData.solutions },
                { label: t('pricing.editor.payment_terms'), value: sheetData.paymentTerms },
                { label: "Roles", value: sheetData.roles },
                { label: "Countries", value: sheetData.countries },
                { label: "Assets", value: sheetData.assets },
                { label: "Seniorities", value: sheetData.seniorities },
                { label: t('pricing.editor.asset_cost_lookup'), value: sheetData.assetCostLookup },
                { label: t('pricing.editor.role_cost_lookup'), value: sheetData.roleCostLookup },
                { label: t('pricing.editor.financing_cost_lookup'), value: sheetData.financingCostLookup },
                { label: t('pricing.editor.campaign_types_pml'), value: sheetData.campaignTypes },
                { label: t('pricing.editor.complexities_pml'), value: sheetData.complexities },
                { label: t('pricing.editor.campaign_type_media_lookup_pml'), value: sheetData.campaignTypeMediaLookup },
                { label: t('pricing.editor.campaign_type_value_lookup_pml'), value: sheetData.campaignTypeValueLookup },
                { label: t('pricing.editor.complexity_client_lookup_pml'), value: sheetData.complexityClientLookup },
                { label: t('pricing.editor.media_value_lookup_pml'), value: sheetData.mediaValueLookup },
                { label: t('pricing.editor.kpi2_media_value_lookup_pml'), value: sheetData.kpi2MediaValueLookup },
                { label: t('pricing.editor.kpi2_complexity_lookup_pml'), value: sheetData.kpi2ComplexityLookup },
                { label: t('pricing.editor.suggested_team_constants_pml'), value: sheetData.suggestedTeamConstants },
                { label: t('pricing.editor.content_client_geos'), value: sheetData.contentClientGeos },
                { label: t('pricing.editor.content_client_complexity'), value: sheetData.contentClientComplexity },
                { label: t('pricing.editor.content_creator_advocate_complexity'), value: sheetData.contentCreatorAdvocateComplexity },
                { label: t('pricing.editor.content_asset_types'), value: sheetData.contentAssetTypes },
                { label: t('pricing.editor.content_partner_countries'), value: sheetData.contentPartnerCountries },
                { label: t('pricing.editor.content_specialist_countries'), value: sheetData.contentSpecialistCountries },
                { label: t('pricing.editor.content_creator_advocate_countries'), value: sheetData.contentCreatorAdvocateCountries },
                { label: t('pricing.editor.content_creator_advocate_lookup'), value: sheetData.contentCreatorAdvocateLookup },
                { label: t('pricing.editor.content_cp_asset_lookup'), value: sheetData.contentCpAssetLookup },
                { label: t('pricing.editor.content_cs_asset_lookup'), value: sheetData.contentCsAssetLookup },
                { label: t('pricing.editor.content_cp_geos_lookup'), value: sheetData.contentCpGeosLookup },
                { label: t('pricing.editor.content_cp_complexity_lookup'), value: sheetData.contentCpComplexityLookup },
                { label: t('pricing.editor.content_cp_client_complexity_lookup'), value: sheetData.contentCpClientComplexityLookup },
                { label: t('pricing.editor.content_cs_geos_lookup'), value: sheetData.contentCsGeosLookup },
                { label: t('pricing.editor.content_cs_complexity_lookup'), value: sheetData.contentCsComplexityLookup },
                { label: t('pricing.editor.content_cp_ssr_lookup'), value: sheetData.contentCpSsrLookup },
                { label: t('pricing.editor.content_cs_ssr_lookup'), value: sheetData.contentCsSsrLookup },
                { label: t('pricing.editor.content_calc_constants'), value: sheetData.contentCalcConstants },
              ];
              return sections.map(({ label, value }) => (
                <div key={label} className="border rounded-md p-3">
                  <div className="text-xs font-semibold text-yellow-500 uppercase tracking-wide mb-2">{label}</div>
                  <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-all bg-muted/40 rounded p-2 max-h-48 overflow-y-auto">
                    {JSON.stringify(value, null, 2)}
                  </pre>
                </div>
              ));
            })() : (
              <p className="text-sm text-muted-foreground">{t('pricing.editor.sheet_data_not_yet_loaded')}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Main content */}
      <div className="flex-1 flex min-h-0">
        {/* Left sidebar - Metrics panel */}
        <aside
          className={`shrink-0 bg-sidebar text-sidebar-foreground flex flex-col overflow-hidden transition-[width] duration-300 ease-in-out ${isMetricsOpen ? 'w-72' : 'w-0'} ${isVersionLocked ? 'pointer-events-none' : ''}`}
          data-testid="metrics-panel"
        >
          {/* Inner fixed-width wrapper so content doesn't reflow during animation */}
          <div className="w-72 flex flex-col h-full">
          {/* Title row — always visible, collapses to just the icon when panel is closed */}
          <div className="shrink-0 px-3 py-3 flex items-center gap-2 border-b border-sidebar-border">
            <button
              onClick={() => setIsMetricsOpen(v => !v)}
              className="text-yellow-400 hover:text-yellow-300 transition-colors shrink-0"
              data-testid="button-metrics-toggle"
              title={isMetricsOpen ? t('pricing.editor.hide_metrics_panel') : t('pricing.editor.show_metrics_panel')}
            >
              <Menu className="w-4 h-4" />
            </button>
            <h2 className="text-lg font-semibold text-yellow-400 flex-1 text-center pr-4 whitespace-nowrap">Metrics</h2>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-4 pb-2 metrics-scroll">
            {(() => {
              const allSolutionsForMetrics = [...teamAssetsSolutions];
              if (isPmlSelected && !allSolutionsForMetrics.includes("Paid Media Lab")) {
                allSolutionsForMetrics.push("Paid Media Lab");
              }
              if (isContentSelected && !allSolutionsForMetrics.includes("Content")) {
                allSolutionsForMetrics.push("Content");
              }
              const isMultiSolution = allSolutionsForMetrics.length > 1;

              if (allSolutionsForMetrics.length === 0) {
                return <p className="text-sm text-muted-foreground">{t('pricing.editor.save_deal_context_and_select_solutions')}</p>;
              }

              // Bundle Pricing values come from state (set by Recalculate, cleared on any pending change)

              return (
                <>
                  {/* ── Desired Margin Section ── */}
                  <div className="space-y-4">
                    <div className="sticky top-0 z-10 bg-[hsl(0,0%,23%)] -mx-4 px-4 py-2">
                      <h3 className="text-sm font-semibold text-yellow-400 uppercase tracking-wide">{t('pricing.editor.desired_margin')}</h3>
                    </div>

                    {allSolutionsForMetrics.map((solution) => {
                      const marginEntry = desiredMargins.find(m => m.solution === solution);
                      const hasData = isVersionLocked || solutionHasData(solution);
                      return (
                        <div key={solution} className="space-y-2" data-testid={`margin-section-${solution}`}>
                          <p className="text-sm font-bold uppercase">{solution}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{t('pricing.editor.desired_margin')}</span>
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              className={`w-20 h-8 text-sm ${hasData ? 'text-black bg-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                              value={hasData ? (marginEntry?.margin || "") : ""}
                              disabled={!hasData}
                              onChange={(e) => {
                                if (!hasData) return;
                                const val = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                                setDesiredMargins(prev => {
                                  const existing = prev.find(m => m.solution === solution);
                                  if (existing) {
                                    return prev.map(m => m.solution === solution ? { ...m, margin: val, suggestedPricing: null } : m);
                                  }
                                  return [...prev, { solution, margin: val, suggestedPricing: null }];
                                });
                                setHasPendingChanges(true);
                              }}
                              data-testid={`input-margin-${solution}`}
                            />
                            <span className="text-sm">%</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{t('pricing.editor.suggested_pricing')}</span>
                            <span className="text-sm font-medium" data-testid={`text-suggested-pricing-${solution}`}>
                              {hasData && marginEntry?.suggestedPricing !== null && marginEntry?.suggestedPricing !== undefined ? `$${formatNumberWithCommas(marginEntry.suggestedPricing)}` : "-"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-400">{t('pricing.editor.monthly_pricing')}</span>
                            <span className="text-sm font-medium text-gray-300" data-testid={`text-monthly-suggested-pricing-${solution}`}>
                              {hasData && marginEntry?.suggestedPricing != null && durationMonths > 0 ? `$${formatNumberWithCommas(marginEntry.suggestedPricing / durationMonths)}` : "-"}
                            </span>
                          </div>
                        </div>
                      );
                    })}

                    {/* Bundle Pricing – auto-derived from per-solution suggested pricings */}
                    {isMultiSolution && (
                      <div className="space-y-2 border-t border-sidebar-border pt-3" data-testid="margin-section-bundle">
                        <p className="text-sm font-bold uppercase text-yellow-300">{t('pricing.editor.bundle_pricing')}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-sm">Pricing</span>
                          <span className="text-sm font-medium" data-testid="text-bundle-suggested-pricing">
                            {bundleSuggestedTotal !== null ? `$${formatNumberWithCommas(bundleSuggestedTotal)}` : "-"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-400">{t('pricing.editor.monthly_pricing')}</span>
                          <span className="text-sm font-medium text-gray-300" data-testid="text-bundle-monthly-suggested-pricing">
                            {bundleSuggestedTotal !== null && durationMonths > 0 ? `$${formatNumberWithCommas(bundleSuggestedTotal / durationMonths)}` : "-"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm">Margin</span>
                          <span className="text-sm font-medium" data-testid="text-bundle-margin-from-suggested">
                            {bundleMarginFromSuggested !== null ? `${Math.round(bundleMarginFromSuggested)}%` : "-"}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Desired Pricing Section ── */}
                  <div className="mt-6 space-y-4">
                    <div className="sticky top-0 z-10 bg-[hsl(0,0%,23%)] -mx-4 px-4 py-2">
                      <h3 className="text-sm font-semibold text-yellow-400 uppercase tracking-wide">{t('pricing.editor.desired_pricing')}</h3>
                    </div>

                    {allSolutionsForMetrics.map((solution) => {
                      const pricingEntry = desiredPricings.find(p => p.solution === solution);
                      const hasData = isVersionLocked || solutionHasData(solution);
                      return (
                        <div key={solution} className="space-y-2" data-testid={`pricing-section-${solution}`}>
                          <p className="text-sm font-bold uppercase">{solution}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{t('pricing.editor.desired_pricing')}</span>
                            <span className="text-sm">$</span>
                            <Input
                              type="number"
                              min={0}
                              className={`w-24 h-8 text-sm ${hasData ? 'text-black bg-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                              value={hasData ? (pricingEntry?.desiredPricing || "") : ""}
                              disabled={!hasData}
                              onChange={(e) => {
                                if (!hasData) return;
                                const val = Math.max(0, parseFloat(e.target.value) || 0);
                                setDesiredPricings(prev => {
                                  const existing = prev.find(p => p.solution === solution);
                                  if (existing) {
                                    return prev.map(p => p.solution === solution ? { ...p, desiredPricing: val, obtainedMargin: null } : p);
                                  }
                                  return [...prev, { solution, desiredPricing: val, obtainedMargin: null }];
                                });
                                setHasPendingChanges(true);
                              }}
                              data-testid={`input-desired-pricing-${solution}`}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-400">{t('pricing.editor.monthly_pricing')}</span>
                            <span className="text-sm font-medium text-gray-300" data-testid={`text-monthly-desired-pricing-${solution}`}>
                              {hasData && pricingEntry?.desiredPricing != null && pricingEntry.desiredPricing > 0 && durationMonths > 0 ? `$${formatNumberWithCommas(pricingEntry.desiredPricing / durationMonths)}` : "-"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{t('pricing.editor.obtained_margin')}</span>
                            <span className="text-sm font-medium" data-testid={`text-obtained-margin-${solution}`}>
                              {hasData && pricingEntry?.obtainedMargin !== null && pricingEntry?.obtainedMargin !== undefined ? `${Math.round(pricingEntry.obtainedMargin)}%` : "-"}
                            </span>
                          </div>
                        </div>
                      );
                    })}

                    {/* Bundle Pricing – auto-derived from per-solution desired pricings */}
                    {isMultiSolution && (
                      <div className="space-y-2 border-t border-sidebar-border pt-3 mb-2" data-testid="pricing-section-bundle">
                        <p className="text-sm font-bold uppercase text-yellow-300">{t('pricing.editor.bundle_pricing')}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-sm">Pricing</span>
                          <span className="text-sm font-medium" data-testid="text-bundle-desired-total">
                            {bundleDesiredTotal !== null ? `$${formatNumberWithCommas(bundleDesiredTotal)}` : "-"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-400">{t('pricing.editor.monthly_pricing')}</span>
                          <span className="text-sm font-medium text-gray-300" data-testid="text-bundle-monthly-desired-pricing">
                            {bundleDesiredTotal !== null && durationMonths > 0 ? `$${formatNumberWithCommas(bundleDesiredTotal / durationMonths)}` : "-"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{t('pricing.editor.obtained_margin')}</span>
                          <span className="text-sm font-medium" data-testid="text-bundle-obtained-margin">
                            {bundleObtainedFromDesired !== null ? `${Math.round(bundleObtainedFromDesired)}%` : "-"}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Sticky Recalculate Button */}
          <div className="shrink-0 border-t border-sidebar-border">
            {(() => {
              const contentErrors: string[] = [];
              if (isContentSelected && activeTab === "team-assets-content") {
                if (!contentVariables.clientGeos) contentErrors.push("Client Geos");
                if (!contentVariables.clientComplexity) contentErrors.push("Client Complexity");
                const missingCountries = contentTeamRows.filter(r => !r.country).map(r => r.role);
                if (missingCountries.length > 0) contentErrors.push(`Country for: ${missingCountries.join(", ")}`);
                const hasAsset = contentAssetRows.some(r => r.assetType && r.assetQuantity >= 1);
                if (!hasAsset) contentErrors.push("At least one Asset with quantity ≥ 1");
                // Creators allocation mismatch
                const creatorsAssetQty = contentAssetRows.filter(r => r.assetType === "Creator Video - Creación").reduce((s, r) => s + (r.assetQuantity || 0), 0);
                const creatorsAllocated = contentCreatorAdvocateDetailData.creators.reduce((s, r) => s + (r.quantity || 0), 0);
                if (creatorsAssetQty > 0 && creatorsAllocated !== creatorsAssetQty) {
                  contentErrors.push(`Creators allocated (${creatorsAllocated}) must match asset quantity (${creatorsAssetQty})`);
                }
                // Advocates allocation mismatch
                const advocatesAssetQty = contentAssetRows.filter(r => r.assetType === "Advocates - Creación").reduce((s, r) => s + (r.assetQuantity || 0), 0);
                const advocatesAllocated = contentCreatorAdvocateDetailData.advocates.reduce((s, r) => s + (r.quantity || 0), 0);
                if (advocatesAssetQty > 0 && advocatesAllocated !== advocatesAssetQty) {
                  contentErrors.push(`Advocates allocated (${advocatesAllocated}) must match asset quantity (${advocatesAssetQty})`);
                }
              }
              const omlErrors: string[] = [];
              if (activeTab === "team-assets" && teamAssetsSolutions.length > 0) {
                const totalAllocation = teamRows.reduce((sum, row) => sum + (row.allocation || 0), 0);
                if (totalAllocation < 1) omlErrors.push("At least 1% Allocation across all Team roles");
              }
              const pmlErrors: string[] = [];
              if (isPmlSelected && activeTab === "team-assets-pml") {
                if (!pmlComplexity) pmlErrors.push("Client Complexity");
                const hasCampaign = pmlCampaignRows.some(r => r.campaignName || r.campaignType || r.campaignsQuantity > 0);
                if (!hasCampaign) pmlErrors.push("At least one campaign with data");
                if (pmlSourcingRows.every(r => !r.country)) pmlErrors.push("Country of Sourcing Team for at least one row");
                const totalAllocation = pmlSourcingRows.reduce((sum, r) => sum + (r.allocation || 0), 0);
                if (totalAllocation < 1) pmlErrors.push("At least 1% Allocation in Sourcing Team");
              }
              const isBlocked = omlErrors.length > 0 || contentErrors.length > 0 || pmlErrors.length > 0;
              return (
                <>
                  {omlErrors.length > 0 && (
                    <div className="mx-4 mt-4 rounded-xl border border-yellow-500/40 bg-gray-900 p-4">
                      <p className="font-bold text-white text-sm mb-3">{t('pricing.editor.you_are_missing_critical_information_in')}</p>
                      <ul className="space-y-2">
                        {omlErrors.map((err, i) => (
                          <li key={i} className="flex items-start gap-2 text-gray-300 text-sm">
                            <span className="text-yellow-400 mt-0.5 shrink-0">•</span>
                            <span>{err}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {pmlErrors.length > 0 && (
                    <div className="mx-4 mt-4 rounded-xl border border-yellow-500/40 bg-gray-900 p-4">
                      <p className="font-bold text-white text-sm mb-3">{t('pricing.editor.you_are_missing_critical_information_in_2')}</p>
                      <ul className="space-y-2">
                        {pmlErrors.map((err, i) => (
                          <li key={i} className="flex items-start gap-2 text-gray-300 text-sm">
                            <span className="text-yellow-400 mt-0.5 shrink-0">•</span>
                            <span>{err}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {contentErrors.length > 0 && (
                    <div className="mx-4 mt-4 rounded-xl border border-yellow-500/40 bg-gray-900 p-4">
                      <p className="font-bold text-white text-sm mb-3">{t('pricing.editor.you_are_missing_critical_information_in_3')}</p>
                      <ul className="space-y-2">
                        {contentErrors.map((err, i) => (
                          <li key={i} className="flex items-start gap-2 text-gray-300 text-sm">
                            <span className="text-yellow-400 mt-0.5 shrink-0">•</span>
                            <span>{err}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="px-4 py-3">
                    <Button
                      onClick={handleRecalculate}
                      disabled={isBlocked}
                      className={`w-full ${isBlocked ? 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-60' : hasPendingChanges ? 'bg-yellow-500 hover:bg-yellow-600 text-black' : 'bg-gray-500 hover:bg-gray-600'}`}
                      data-testid="button-recalculate"
                    >
                      Recalculate
                    </Button>
                  </div>
                </>
              );
            })()}
          </div>
          </div>{/* end inner fixed-width wrapper */}
        </aside>

        {/* Main area */}
        <main className="flex-1 flex flex-col">
          {/* Tabs */}
          <div className="flex border-b">
            {!isMetricsOpen && (
              <button
                onClick={() => setIsMetricsOpen(true)}
                className="px-3 py-3 text-yellow-400 hover:text-yellow-300 bg-sidebar hover:bg-sidebar/80 border-r border-sidebar-border transition-colors shrink-0 flex items-center"
                data-testid="button-metrics-open"
                title={t('pricing.editor.show_metrics_panel')}
              >
                <Menu className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => handleTabClick("deal-context")}
              className={getTabClassName("deal-context")}
              data-testid="tab-deal-context"
            >
              {t('pricing.editor.deal_context')}
            </button>
            {/* Show Team & Assets Content tab only if Content is selected */}
            {isContentSelected && (
              <button
                onClick={() => handleTabClick("team-assets-content")}
                className={getTabClassName("team-assets-content")}
                data-testid="tab-team-assets-content"
              >
                <span className="flex items-center justify-center gap-2">
                  CONTENT
                  {!isDealContextSaved && <Lock className="w-4 h-4" />}
                </span>
              </button>
            )}
            {/* Show Team & Assets PML tab only if Paid Media Lab is selected */}
            {isPmlSelected && (
              <button
                onClick={() => handleTabClick("team-assets-pml")}
                className={getTabClassName("team-assets-pml")}
                data-testid="tab-team-assets-pml"
              >
                <span className="flex items-center justify-center gap-2">
                  PML
                  {!isDealContextSaved && <Lock className="w-4 h-4" />}
                </span>
              </button>
            )}
            {/* Show Team & Assets tab only if there are non-PML, non-Content solutions */}
            {teamAssetsSolutions.length > 0 && (
              <button
                onClick={() => handleTabClick("team-assets")}
                className={getTabClassName("team-assets")}
                data-testid="tab-team-assets"
              >
                <span className="flex items-center justify-center gap-2">
                  {/* i18n-ok: nombre de solución, vocabulario de la planilla (bifurca lógica) */}
                  OML & OTHER SOLUTIONS
                  {!isDealContextSaved && <Lock className="w-4 h-4" />}
                </span>
              </button>
            )}
            <button
              onClick={() => handleTabClick("margins")}
              className={getTabClassName("margins")}
              data-testid="tab-margins"
            >
              <span className="flex items-center justify-center gap-2">
                MARGINS
                {!isDealContextSaved && <Lock className="w-4 h-4" />}
              </span>
            </button>
          </div>

          {/* Tab content */}
          <div className={`flex-1 p-8 overflow-auto ${isVersionLocked ? 'pointer-events-none opacity-75' : ''}`}>
            {activeTab === "deal-context" && (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-6">
                    {/* Left column */}
                    <div className="space-y-6">
                      {/* Client - searchable dropdown */}
                      <FormField
                        control={form.control}
                        name="client"
                        render={({ field }) => {
                          // Hooks adentro del render={} de FormField (react-hooks/rules-of-hooks).
                          // render={} de FormField. Funciona porque Controller llama render()
                          // sincrónicamente y los hooks se pegan a SU fiber, pero es frágil. El fix
                          // real es extraer un componente <ClientSearchField>; no se hace acá porque
                          // PricingEditor no se refactoriza sin snapshot tests (ver memoria del repo).
                          // eslint-disable-next-line react-hooks/rules-of-hooks
                          const [clientSearch, setClientSearch] = useState("");
                          // eslint-disable-next-line react-hooks/rules-of-hooks -- idem arriba
                          const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
                          // eslint-disable-next-line react-hooks/rules-of-hooks -- idem arriba
                          const clientRef = useRef<HTMLDivElement>(null);

                          // eslint-disable-next-line react-hooks/rules-of-hooks -- idem arriba
                          useEffect(() => {
                            const handleClickOutside = (event: MouseEvent) => {
                              if (clientRef.current && !clientRef.current.contains(event.target as Node)) {
                                setClientDropdownOpen(false);
                              }
                            };
                            document.addEventListener("mousedown", handleClickOutside);
                            return () => document.removeEventListener("mousedown", handleClickOutside);
                          }, []);

                          const OTHER_NEW_CLIENT = "Other New Client";
                          const allClients = sheetData?.clients || [];
                          const sortedClients = [...allClients].sort((a, b) => a.name.localeCompare(b.name));
                          const otherNewClient = sortedClients.find(c => c.name === OTHER_NEW_CLIENT);
                          const regularClients = sortedClients.filter(c => c.name !== OTHER_NEW_CLIENT);
                          const orderedClients = otherNewClient ? [otherNewClient, ...regularClients] : regularClients;
                          const filteredClients = orderedClients.filter((client) =>
                            client.name.toLowerCase().includes(clientSearch.toLowerCase())
                          );

                          return (
                            <FormItem className="flex items-center gap-4">
                              <FormLabel className="w-32 text-right font-medium">Client:</FormLabel>
                              <div className="flex-1 relative" ref={clientRef}>
                                <FormControl>
                                  <div
                                    className="flex items-center border rounded-md bg-background cursor-pointer"
                                    onClick={() => setClientDropdownOpen(!clientDropdownOpen)}
                                    data-testid="select-client"
                                  >
                                    <Search className="h-4 w-4 ml-3 text-muted-foreground shrink-0" />
                                    <input
                                      className="flex h-10 w-full bg-transparent px-3 py-2 text-sm outline-hidden placeholder:text-muted-foreground"
                                      placeholder={field.value || t('pricing.editor.search_client')}
                                      value={clientDropdownOpen ? clientSearch : (field.value || "")}
                                      onChange={(e) => {
                                        setClientSearch(e.target.value);
                                        if (!clientDropdownOpen) setClientDropdownOpen(true);
                                      }}
                                      onFocus={() => setClientDropdownOpen(true)}
                                      onClick={(e) => e.stopPropagation()}
                                      data-testid="input-client-search"
                                    />
                                    <ChevronDown className="h-4 w-4 mr-3 text-muted-foreground shrink-0" />
                                  </div>
                                </FormControl>
                                {clientDropdownOpen && (
                                  <div className="absolute z-50 mt-1 w-full bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
                                    {isLoadingData ? (
                                      <div className="p-3 text-sm text-muted-foreground">Loading...</div>
                                    ) : isDataError ? (
                                      <div className="p-3 text-sm text-destructive">{t('pricing.editor.error_loading_data')}</div>
                                    ) : filteredClients.length === 0 ? (
                                      <div className="p-3 text-sm text-muted-foreground">{t('pricing.editor.no_clients_found')}</div>
                                    ) : (
                                      filteredClients.map((client) => {
                                        const isOtherNew = client.name === OTHER_NEW_CLIENT;
                                        const isSelected = field.value === client.name;
                                        return (
                                        <div
                                          key={client.name}
                                          className={`flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-accent ${isSelected ? "bg-accent" : ""}`}
                                          onClick={() => {
                                            field.onChange(client.name);
                                            setClientSearch("");
                                            setClientDropdownOpen(false);
                                            if (client.name === OTHER_NEW_CLIENT) {
                                              setShowHubSpotReminder(true);
                                            }
                                          }}
                                          data-testid={`option-client-${client.name}`}
                                        >
                                          {isSelected && <Check className="h-4 w-4 mr-2 shrink-0" />}
                                          <span className={`${isSelected ? "" : "ml-6"} ${isOtherNew ? "text-black font-extrabold dark:text-white" : ""}`}>{client.name}</span>
                                        </div>
                                        );
                                      })
                                    )}
                                  </div>
                                )}
                                <FormMessage />
                              </div>
                            </FormItem>
                          );
                        }}
                      />

                      {/* Deal Name (was Version Name) */}
                      <FormField
                        control={form.control}
                        name="versionName"
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-4">
                            <FormLabel className="w-32 text-right font-medium">{t('pricing.editor.deal_name')}</FormLabel>
                            <div className="flex-1">
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder={t('pricing.editor.write_the_deal_name')}
                                  data-testid="input-deal-name"
                                />
                              </FormControl>
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />

                      {/* Segment */}
                      <FormField
                        control={form.control}
                        name="segment"
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-4">
                            <FormLabel className="w-32 text-right font-medium">Segment:</FormLabel>
                            <div className="flex-1">
                              <FormControl>
                                {selectedClient === "Other New Client" ? (
                                  <Select
                                    value={field.value || ""}
                                    onValueChange={field.onChange}
                                    disabled={isVersionLocked}
                                  >
                                    <SelectTrigger data-testid="select-segment">
                                      <SelectValue placeholder={t('pricing.editor.select_segment')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {(sheetData?.segments ?? []).map((seg) => (
                                        <SelectItem key={seg} value={seg}>{seg}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Input
                                    {...field}
                                    disabled
                                    placeholder=""
                                    className="bg-muted"
                                    data-testid="input-segment"
                                  />
                                )}
                              </FormControl>
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />

                      {/* Currency */}
                      <FormField
                        control={form.control}
                        name="currency"
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-4">
                            <FormLabel className="w-32 text-right font-medium">Currency:</FormLabel>
                            <div className="flex-1">
                              {isVersionLocked ? (
                                <div className="h-10 flex items-center px-3 border rounded-md bg-muted text-sm" data-testid="select-currency">{field.value || '-'}</div>
                              ) : (
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger data-testid="select-currency">
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {isLoadingData ? (
                                    <SelectItem value="loading" disabled>Loading...</SelectItem>
                                  ) : isDataError ? (
                                    <SelectItem value="error" disabled>{t('pricing.editor.error_loading_data')}</SelectItem>
                                  ) : (
                                    sheetData?.currencies?.map((currency) => (
                                      <SelectItem key={currency} value={currency}>
                                        {currency}
                                      </SelectItem>
                                    ))
                                  )}
                                </SelectContent>
                              </Select>
                              )}
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />

                      {/* Society */}
                      <FormField
                        control={form.control}
                        name="society"
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-4">
                            <FormLabel className="w-32 text-right font-medium">Society:</FormLabel>
                            <div className="flex-1">
                              {isVersionLocked ? (
                                <div className="h-10 flex items-center px-3 border rounded-md bg-muted text-sm" data-testid="select-society">{field.value || '-'}</div>
                              ) : (
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger data-testid="select-society">
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {isLoadingData ? (
                                    <SelectItem value="loading" disabled>Loading...</SelectItem>
                                  ) : isDataError ? (
                                    <SelectItem value="error" disabled>{t('pricing.editor.error_loading_data')}</SelectItem>
                                  ) : (
                                    sheetData?.societies?.map((society) => (
                                      <SelectItem key={society} value={society}>
                                        {society}
                                      </SelectItem>
                                    ))
                                  )}
                                </SelectContent>
                              </Select>
                              )}
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />

                      {/* Revenue Type */}
                      <FormField
                        control={form.control}
                        name="revenueType"
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-4">
                            <FormLabel className="w-32 text-right font-medium">{t('pricing.editor.revenue_type')}</FormLabel>
                            <div className="flex-1">
                              {isVersionLocked ? (
                                <div className="h-10 flex items-center px-3 border rounded-md bg-muted text-sm" data-testid="select-revenue-type">{field.value || '-'}</div>
                              ) : (
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger data-testid="select-revenue-type">
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {isLoadingData ? (
                                    <SelectItem value="loading" disabled>Loading...</SelectItem>
                                  ) : isDataError ? (
                                    <SelectItem value="error" disabled>{t('pricing.editor.error_loading_data')}</SelectItem>
                                  ) : (
                                    sheetData?.revenueTypes?.map((type) => (
                                      <SelectItem key={type} value={type}>
                                        {type}
                                      </SelectItem>
                                    ))
                                  )}
                                </SelectContent>
                              </Select>
                              )}
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />

                    </div>

                    {/* Right column */}
                    <div className="space-y-6">
                      {/* Deal Type */}
                      <FormField
                        control={form.control}
                        name="dealType"
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-4">
                            <FormLabel className="w-32 text-right font-medium">{t('pricing.editor.deal_type')}</FormLabel>
                            <div className="flex-1">
                              {isVersionLocked ? (
                                <div className="h-10 flex items-center px-3 border rounded-md bg-muted text-sm" data-testid="select-deal-type">{field.value || '-'}</div>
                              ) : (
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger data-testid="select-deal-type">
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {isLoadingData ? (
                                    <SelectItem value="loading" disabled>Loading...</SelectItem>
                                  ) : isDataError ? (
                                    <SelectItem value="error" disabled>{t('pricing.editor.error_loading_data')}</SelectItem>
                                  ) : (
                                    sheetData?.dealTypes?.map((type) => (
                                      <SelectItem key={type} value={type}>
                                        {type}
                                      </SelectItem>
                                    ))
                                  )}
                                </SelectContent>
                              </Select>
                              )}
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />

                      {/* Solutions - Multi-select */}
                      <FormField
                        control={form.control}
                        name="solutions"
                        render={() => (
                          <FormItem className="flex items-start gap-4">
                            <FormLabel className="w-32 text-right font-medium pt-2">{isSingleDeal ? "Solution:" : "Solution/s:"}</FormLabel>
                            <div className="flex-1">
                              <div className="border rounded-md p-3 min-h-[80px] space-y-2">
                                <div className="flex flex-wrap gap-2">
                                  {selectedSolutions.map((solution) => (
                                    <Badge 
                                      key={solution} 
                                      variant="secondary"
                                      className="cursor-pointer"
                                      onClick={() => toggleSolution(solution)}
                                      data-testid={`badge-solution-${solution}`}
                                    >
                                      {solution} ×
                                    </Badge>
                                  ))}
                                </div>
                                {!(isSingleDeal && selectedSolutions.length >= 1) && (
                                <Select value="" onValueChange={(value) => {
                                  if (!selectedSolutions.includes(value)) {
                                    toggleSolution(value);
                                  }
                                }}>
                                  <SelectTrigger data-testid="select-solutions">
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {isLoadingData ? (
                                      <SelectItem value="loading" disabled>Loading...</SelectItem>
                                    ) : isDataError ? (
                                      <SelectItem value="error" disabled>{t('pricing.editor.error_loading_data')}</SelectItem>
                                    ) : (
                                      sheetData?.solutions?.map((solution) => (
                                        <SelectItem 
                                          key={solution} 
                                          value={solution}
                                          disabled={selectedSolutions.includes(solution)}
                                        >
                                          {solution}
                                        </SelectItem>
                                      ))
                                    )}
                                  </SelectContent>
                                </Select>
                                )}
                              </div>
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />

                      {/* Duration/months */}
                      <FormField
                        control={form.control}
                        name="durationMonths"
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-4">
                            <FormLabel className="w-32 text-right font-medium">Duration/months:</FormLabel>
                            <div className="flex-1">
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  max={60}
                                  placeholder=""
                                  value={field.value || ""}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === "") {
                                      field.onChange(undefined);
                                    } else {
                                      const num = parseInt(val);
                                      if (!isNaN(num) && num >= 1 && num <= 60) {
                                        field.onChange(num);
                                      }
                                    }
                                    setHasPendingChanges(true);
                                    setDesiredMargins(prev => prev.map(m => ({ ...m, suggestedPricing: null })));
                                    setDesiredPricings(prev => prev.map(p => ({ ...p, obtainedMargin: null })));
                                  }}
                                  data-testid="input-duration"
                                />
                              </FormControl>
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />

                      {/* Payment Terms */}
                      <FormField
                        control={form.control}
                        name="paymentTerms"
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-4">
                            <FormLabel className="w-32 text-right font-medium">{t('pricing.editor.payment_terms_days')}</FormLabel>
                            <div className="flex-1">
                              {isVersionLocked ? (
                                <div className="h-10 flex items-center px-3 border rounded-md bg-muted text-sm" data-testid="select-payment-terms">{field.value || '-'}</div>
                              ) : (
                              <Select onValueChange={(value) => {
                                field.onChange(value);
                                setHasPendingChanges(true);
                                setDesiredMargins(prev => prev.map(m => ({ ...m, suggestedPricing: null })));
                                setDesiredPricings(prev => prev.map(p => ({ ...p, obtainedMargin: null })));
                              }} value={field.value}>
                                <FormControl>
                                  <SelectTrigger data-testid="select-payment-terms">
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {isLoadingData ? (
                                    <SelectItem value="loading" disabled>Loading...</SelectItem>
                                  ) : isDataError ? (
                                    <SelectItem value="error" disabled>{t('pricing.editor.error_loading_data')}</SelectItem>
                                  ) : (
                                    sheetData?.paymentTerms?.map((term) => (
                                      <SelectItem key={term} value={term}>
                                        {term}
                                      </SelectItem>
                                    ))
                                  )}
                                </SelectContent>
                              </Select>
                              )}
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />

                      {/* Comments */}
                      <FormField
                        control={form.control}
                        name="comments"
                        render={({ field }) => (
                          <FormItem className="flex items-start gap-4">
                            <FormLabel className="w-32 text-right font-medium pt-2">Comments:</FormLabel>
                            <div className="flex-1">
                              <FormControl>
                                <Textarea
                                  {...field}
                                  placeholder={t('pricing.editor.add_any_comments_to_help_you')}
                                  className="min-h-[80px]"
                                  data-testid="input-comments"
                                />
                              </FormControl>
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {!isDealContextSaved && (
                    <div className="flex justify-center pt-8">
                      <Button
                        type="submit"
                        size="lg"
                        className="w-64"
                        disabled={saveVersionMutation.isPending}
                        data-testid="button-create-pricing"
                      >
                        {saveVersionMutation.isPending ? t('pricing.editor.creating') : t('pricing.editor.create_pricing')}
                      </Button>
                    </div>
                  )}
                </form>
              </Form>
            )}

            {activeTab === "team-assets" && isDealContextSaved && (
              <div className="space-y-8" data-testid="team-assets-content">
                {teamAssetsSolutions.length === 0 ? (
                  <Card className="p-8 text-center">
                    <h2 className="text-xl font-semibold mb-4">{t('pricing.editor.no_applicable_solutions')}</h2>
                    <p className="text-muted-foreground">
                      {t('pricing.editor.the_selected_solutions_do_not_require')}
                    </p>
                    <p className="text-muted-foreground mt-2">
                      {t('pricing.editor.this_section_applies_to_solutions_other')}
                    </p>
                  </Card>
                ) : (
                  <>
                    {/* Team Chart Section */}
                    <Card className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h2 className="text-lg font-semibold text-foreground mb-1" data-testid="text-team-chart-title">{t('pricing.editor.select_your_desired_team')}</h2>
                          <p className="text-sm text-muted-foreground italic">{t('pricing.editor.if_you_can_t_find_the')}</p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          onClick={addTeamRow}
                          data-testid="button-add-team-row"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          {t('pricing.editor.add_row')}
                        </Button>
                      </div>
                      
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse" data-testid="team-table">
                          <thead>
                            <tr className="bg-primary text-primary-foreground">
                              <th className="border border-border p-2 text-left min-w-[150px]">Role</th>
                              <th className="border border-border p-2 text-left min-w-[120px]">Country</th>
                              <th className="border border-border p-2 text-left min-w-[150px]">Solution</th>
                              <th className="border border-border p-2 text-center min-w-[120px]">{t('pricing.editor.monthly_allocation')}</th>
                              <th className="border border-border p-2 w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {teamRows.length === 0 ? (
                              <tr>
                                <td colSpan={5} className="border border-border p-4 text-center text-muted-foreground">
                                  {t('pricing.editor.no_team_members_added_click_add')}
                                </td>
                              </tr>
                            ) : (
                              teamRows.map((row) => (
                                <tr key={row.id} data-testid={`team-row-${row.id}`}>
                                  <td className="border border-border p-1">
                                    <Select
                                      value={row.role}
                                      onValueChange={(value) => {
                                        updateTeamRow(row.id, 'role', value);
                                        const availableCountries = getCountriesForRole(value);
                                        if (row.country && !availableCountries.includes(row.country)) {
                                          updateTeamRow(row.id, 'country', '');
                                        }
                                      }}
                                    >
                                      <SelectTrigger className="h-8" data-testid={`select-role-${row.id}`}>
                                        <SelectValue placeholder="Select" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {getRolesForCountry(row.country).map((role) => (
                                          <SelectItem key={role} value={role}>{role}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </td>
                                  <td className="border border-border p-1">
                                    <Select
                                      value={row.country}
                                      onValueChange={(value) => {
                                        updateTeamRow(row.id, 'country', value);
                                        const availableRoles = getRolesForCountry(value);
                                        if (row.role && !availableRoles.includes(row.role)) {
                                          updateTeamRow(row.id, 'role', '');
                                        }
                                      }}
                                    >
                                      <SelectTrigger className="h-8" data-testid={`select-country-${row.id}`}>
                                        <SelectValue placeholder="Select" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {getCountriesForRole(row.role).map((country) => (
                                          <SelectItem key={country} value={country}>{country}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </td>
                                  <td className="border border-border p-1">
                                    <Select
                                      value={row.group}
                                      onValueChange={(value) => updateTeamRow(row.id, 'group', value)}
                                    >
                                      <SelectTrigger className="h-8" data-testid={`select-group-${row.id}`}>
                                        <SelectValue placeholder="Select" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {teamAssetsSolutions.map((solution) => (
                                          <SelectItem key={solution} value={solution}>{solution}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </td>
                                  <td className="border border-border p-1">
                                    <Input
                                      type="number"
                                      className="h-8 text-center text-sm"
                                      min={0}
                                      max={1000}
                                      value={row.allocation || ""}
                                      onChange={(e) => {
                                        const val = Math.min(1000, Math.max(0, parseInt(e.target.value) || 0));
                                        updateTeamRow(row.id, 'allocation', val);
                                      }}
                                      placeholder="%"
                                      data-testid={`input-allocation-${row.id}`}
                                    />
                                  </td>
                                  <td className="border border-border p-1 text-center">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => deleteTeamRow(row.id)}
                                      className="h-8 w-8"
                                      data-testid={`button-delete-team-${row.id}`}
                                    >
                                      <Trash2 className="w-4 h-4 text-destructive" />
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </Card>

                    {/* Assets Chart Section - only when Owned Media Lab is selected */}
                    {selectedSolutions.includes("Owned Media Lab") && (
                    <Card className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-foreground" data-testid="text-assets-chart-title">Assets</h2>
                        <Button
                          type="button"
                          size="sm"
                          onClick={addAssetRow}
                          data-testid="button-add-asset-row"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          {t('pricing.editor.add_row')}
                        </Button>
                      </div>
                      
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse" data-testid="assets-table">
                          <thead>
                            <tr className="bg-primary text-primary-foreground">
                              <th className="border border-border p-2 text-left min-w-[200px]">Assets</th>
                              <th className="border border-border p-2 text-left min-w-[120px]">Seniority</th>
                              <th className="border border-border p-2 text-left min-w-[100px]">{t('pricing.editor.unit_cost')}</th>
                              <th className="border border-border p-2 text-left min-w-[150px]">Solution</th>
                              <th className="border border-border p-2 text-center min-w-[120px]">{t('pricing.editor.total_assets_quantity')}</th>
                              <th className="border border-border p-2 w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {assetRows.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="border border-border p-4 text-center text-muted-foreground">
                                  {t('pricing.editor.no_assets_added_click_add_row')}
                                </td>
                              </tr>
                            ) : (
                              assetRows.map((row) => (
                                <tr key={row.id} data-testid={`asset-row-${row.id}`}>
                                  <td className="border border-border p-1">
                                    <Select
                                      value={row.asset}
                                      onValueChange={(value) => updateAssetRow(row.id, 'asset', value)}
                                    >
                                      <SelectTrigger className="h-8" data-testid={`select-asset-${row.id}`}>
                                        <SelectValue placeholder="Select" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {sheetData?.assets?.map((asset) => (
                                          <SelectItem key={asset} value={asset}>{asset}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </td>
                                  <td className="border border-border p-1">
                                    <Select
                                      value={row.seniority}
                                      onValueChange={(value) => updateAssetRow(row.id, 'seniority', value)}
                                    >
                                      <SelectTrigger className="h-8" data-testid={`select-seniority-${row.id}`}>
                                        <SelectValue placeholder="Select" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {sheetData?.seniorities?.map((seniority) => (
                                          <SelectItem key={seniority} value={seniority}>{seniority}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </td>
                                  <td className="border border-border p-1">
                                    <Input
                                      className="h-8 bg-muted"
                                      value={row.unitCost !== null ? row.unitCost : ""}
                                      disabled
                                      placeholder="Auto"
                                      data-testid={`input-unit-cost-${row.id}`}
                                    />
                                  </td>
                                  <td className="border border-border p-1">
                                    <Input
                                      className="h-8 bg-muted"
                                      value="Owned Media Lab"
                                      disabled
                                      data-testid={`input-asset-solution-${row.id}`}
                                    />
                                  </td>
                                  <td className="border border-border p-1">
                                    <Input
                                      type="number"
                                      className="h-8 text-center text-sm"
                                      min={0}
                                      max={1000}
                                      value={row.totalQuantity || ""}
                                      onChange={(e) => {
                                        const val = Math.min(1000, Math.max(0, parseInt(e.target.value) || 0));
                                        updateAssetRow(row.id, 'totalQuantity', val);
                                      }}
                                      placeholder="Qty"
                                      data-testid={`input-total-quantity-${row.id}`}
                                    />
                                  </td>
                                  <td className="border border-border p-1 text-center">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => deleteAssetRow(row.id)}
                                      className="h-8 w-8"
                                      data-testid={`button-delete-asset-${row.id}`}
                                    >
                                      <Trash2 className="w-4 h-4 text-destructive" />
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                    )}

                    {/* Other Costs Section */}
                    <Card className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-foreground" data-testid="text-other-costs-title">{t('pricing.editor.select_potential_other_costs')}</h2>
                        <Button
                          type="button"
                          size="sm"
                          onClick={addOtherCostRow}
                          disabled={isVersionLocked}
                          data-testid="button-add-other-cost-row"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          {t('pricing.editor.add_row')}
                        </Button>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse" data-testid="other-costs-table">
                          <thead>
                            <tr className="bg-primary text-primary-foreground">
                              <th className="border border-border p-2 text-left min-w-[200px]">Description</th>
                              <th className="border border-border p-2 text-left min-w-[150px]">Solution</th>
                              <th className="border border-border p-2 text-center min-w-[150px]">{t('pricing.editor.monthly_cost_usd')}</th>
                              <th className="border border-border p-2 w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {otherCostRows.length === 0 ? (
                              <tr>
                                <td colSpan={4} className="border border-border p-4 text-center text-muted-foreground">
                                  {t('pricing.editor.no_other_costs_added_click_add')}
                                </td>
                              </tr>
                            ) : (
                              otherCostRows.map((row) => (
                                <tr key={row.id} data-testid={`other-cost-row-${row.id}`}>
                                  <td className="border border-border p-1">
                                    <Input
                                      className="h-8 text-sm"
                                      value={row.description}
                                      onChange={(e) => updateOtherCostRow(row.id, 'description', e.target.value)}
                                      placeholder="Description"
                                      disabled={isVersionLocked}
                                      data-testid={`input-other-cost-description-${row.id}`}
                                    />
                                  </td>
                                  <td className="border border-border p-1">
                                    <Select
                                      value={row.solution}
                                      onValueChange={(value) => updateOtherCostRow(row.id, 'solution', value)}
                                      disabled={isVersionLocked}
                                    >
                                      <SelectTrigger className="h-8" data-testid={`select-other-cost-solution-${row.id}`}>
                                        <SelectValue placeholder="Select" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {teamAssetsSolutions.map((sol) => (
                                          <SelectItem key={sol} value={sol}>{sol}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </td>
                                  <td className="border border-border p-1">
                                    <Input
                                      type="number"
                                      className="h-8 text-center text-sm"
                                      min={0}
                                      value={row.monthlyCostUSD || ""}
                                      onChange={(e) => {
                                        const val = Math.max(0, parseFloat(e.target.value) || 0);
                                        updateOtherCostRow(row.id, 'monthlyCostUSD', val);
                                      }}
                                      placeholder="0"
                                      disabled={isVersionLocked}
                                      data-testid={`input-other-cost-monthly-${row.id}`}
                                    />
                                  </td>
                                  <td className="border border-border p-1 text-center">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => deleteOtherCostRow(row.id)}
                                      disabled={isVersionLocked}
                                      className="h-8 w-8"
                                      data-testid={`button-delete-other-cost-${row.id}`}
                                    >
                                      <Trash2 className="w-4 h-4 text-destructive" />
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  </>
                )}
              </div>
            )}

            {activeTab === "team-assets-pml" && isDealContextSaved && isPmlSelected && (
              <div className="space-y-8" data-testid="team-assets-pml-content">
                {/* Campaign Section */}
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-foreground">Campaign</h2>
                    <Button
                      type="button"
                      size="sm"
                      onClick={addPmlCampaignRow}
                      data-testid="button-add-pml-campaign"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      {t('pricing.editor.add_row')}
                    </Button>
                  </div>

                  <div className="flex items-center gap-3 mb-4">
                    <label className="text-sm font-medium text-foreground whitespace-nowrap">{t('pricing.editor.client_complexity')}</label>
                    <Select
                      value={pmlComplexity}
                      onValueChange={(value) => {
                        setPmlComplexity(value);
                        setHasPendingChanges(true);
                      }}
                    >
                      <SelectTrigger className="h-8 w-48" data-testid="select-pml-complexity">
                        <SelectValue placeholder={t('pricing.editor.select_complexity')} />
                      </SelectTrigger>
                      <SelectContent>
                        {sheetData?.complexities?.map((complexity) => (
                          <SelectItem key={complexity} value={complexity}>{complexity}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="overflow-x-auto">
                  <table className="w-full border-collapse" data-testid="pml-campaign-table">
                    <thead>
                      <tr className="bg-primary text-primary-foreground">
                        <th className="border border-border p-2 text-center">{t('pricing.editor.campaign_name')}</th>
                        <th className="border border-border p-2 text-center">{t('pricing.editor.campaign_type')}</th>
                        <th className="border border-border p-2 text-center">{t('pricing.editor.campaign_media')}</th>
                        <th className="border border-border p-2 text-center">{t('pricing.editor.campaigns_q')}</th>
                        <th className="border border-border p-2 text-center"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pmlCampaignRows.length > 0 && (
                        pmlCampaignRows.map((row) => (
                          <tr key={row.id} data-testid={`pml-campaign-row-${row.id}`}>
                            <td className="border border-border p-1">
                              <Input
                                className="h-8"
                                value={row.campaignName}
                                onChange={(e) => updatePmlCampaignRow(row.id, 'campaignName', e.target.value)}
                                placeholder={t('pricing.editor.enter_name')}
                                data-testid={`input-pml-campaign-name-${row.id}`}
                              />
                            </td>
                            <td className="border border-border p-1">
                              <Select
                                value={row.campaignType}
                                onValueChange={(value) => updatePmlCampaignRow(row.id, 'campaignType', value)}
                              >
                                <SelectTrigger className="h-8" data-testid={`select-pml-campaign-type-${row.id}`}>
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                  {[...(sheetData?.campaignTypes ?? [])].sort((a, b) => a.localeCompare(b)).map((type) => (
                                    <SelectItem key={type} value={type}>{type}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                className="h-8 bg-muted"
                                value={row.campaignMedia}
                                readOnly
                                placeholder="Auto"
                                data-testid={`input-pml-campaign-media-${row.id}`}
                              />
                            </td>
                            <td className="border border-border p-1">
                              <Input
                                type="number"
                                className="h-8 text-center text-sm"
                                min={0}
                                value={row.campaignsQuantity || ""}
                                onChange={(e) => updatePmlCampaignRow(row.id, 'campaignsQuantity', parseInt(e.target.value) || 0)}
                                placeholder="0"
                                data-testid={`input-pml-campaigns-quantity-${row.id}`}
                              />
                            </td>
                            <td className="border border-border p-1 text-center">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => deletePmlCampaignRow(row.id)}
                                className="h-8 w-8"
                                data-testid={`button-delete-pml-campaign-${row.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  </div>
                </Card>

                {/* Suggested Team Section */}
                <Card className="p-6">
                  <h2 className="text-lg font-semibold text-foreground mb-1">{t('pricing.editor.suggested_team')}</h2>
                  <p className="text-sm text-muted-foreground italic mb-4">{t('pricing.editor.the_expected_team_for_delivery_calculated')}</p>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse table-fixed" data-testid="pml-suggested-team-table">
                      <colgroup>
                        <col /><col /><col /><col />
                      </colgroup>
                      <thead>
                        <tr className="bg-primary text-primary-foreground">
                          <th className="border border-border p-2 text-center" colSpan={4}>{t('pricing.editor.allocation')}</th>
                        </tr>
                        <tr className="bg-muted">
                          <th className="border border-border p-2 text-center font-semibold">Analyst</th>
                          {/* i18n-ok: vocabulario de la planilla de costos, no se traduce */}
                          <th className="border border-border p-2 text-center font-semibold">Sr. Analyst</th>
                          <th className="border border-border p-2 text-center font-semibold">Strategist</th>
                          <th className="border border-border p-2 text-center font-semibold">Lead</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr data-testid="pml-suggested-team-row">
                          <td className="border border-border p-2 text-center">
                            <span className="text-sm font-medium" data-testid="text-suggested-analyst">-</span>
                          </td>
                          <td className="border border-border p-2 text-center">
                            <span className="text-sm font-medium" data-testid="text-suggested-sr-analyst">
                              {pmlSuggestedTeam.srAnalystAllocation !== null ? `${Math.round(pmlSuggestedTeam.srAnalystAllocation)}%` : '-'}
                            </span>
                          </td>
                          <td className="border border-border p-2 text-center">
                            <span className="text-sm font-medium" data-testid="text-suggested-strategist">-</span>
                          </td>
                          <td className="border border-border p-2 text-center">
                            <span className="text-sm font-medium" data-testid="text-suggested-lead">
                              {pmlSuggestedTeam.leadAllocation !== null ? `${Math.round(pmlSuggestedTeam.leadAllocation)}%` : '-'}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card>

                {/* Sourcing Team Section */}
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-semibold text-foreground mb-1">{t('pricing.editor.sourcing_team')}</h2>
                      <p className="text-sm text-muted-foreground italic">{t('pricing.editor.choose_the_actual_team_you_will')}</p>
                    </div>
                    {!isVersionLocked && (
                      <Button type="button" variant="outline" size="sm" onClick={addPmlSourcingRow} data-testid="button-add-pml-sourcing-row" style={{ backgroundColor: '#262626', color: '#fff', borderColor: '#262626' }}>
                        {t('pricing.editor.add_row_2')}
                      </Button>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse" data-testid="pml-sourcing-team-table">
                      <thead>
                        <tr className="bg-primary text-primary-foreground">
                          <th className="border border-border p-2 text-left text-sm">Country</th>
                          <th className="border border-border p-2 text-left text-sm">Role</th>
                          <th className="border border-border p-2 text-center text-sm">{t('pricing.editor.allocation')}</th>
                          {!isVersionLocked && <th className="border border-border p-2 w-10"></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {pmlSourcingRows.map((row) => (
                          <tr key={row.id} data-testid={`pml-sourcing-row-${row.id}`}>
                            <td className="border border-border p-1">
                              <Select
                                value={row.country}
                                onValueChange={(v) => updatePmlSourcingRow(row.id, 'country', v)}
                                disabled={isVersionLocked}
                              >
                                <SelectTrigger className="h-8" data-testid={`select-pml-sourcing-country-${row.id}`}>
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                  {uniqueCountries.map((c) => (
                                    <SelectItem key={c} value={c}>{c}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="border border-border p-1">
                              <Select
                                value={row.role}
                                onValueChange={(v) => updatePmlSourcingRow(row.id, 'role', v)}
                                disabled={isVersionLocked}
                              >
                                <SelectTrigger className="h-8" data-testid={`select-pml-sourcing-role-${row.id}`}>
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                  {/* i18n-ok: vocabulario de la planilla de costos, no se traduce */}
                                  <SelectItem value="Performance Specialist">Performance Specialist</SelectItem>
                                  <SelectItem value="Performance Specialist Sr">Performance Specialist Sr</SelectItem> {/* i18n-ok: vocabulario de la planilla */}
                                  <SelectItem value="Performance Lead/Strategist">Performance Lead/Strategist</SelectItem> {/* i18n-ok: vocabulario de la planilla */}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="border border-border p-1 text-center">
                              <Input
                                type="number"
                                className="h-8 text-center text-sm"
                                min={0}
                                value={row.allocation || ""}
                                onChange={(e) => updatePmlSourcingRow(row.id, 'allocation', parseInt(e.target.value) || 0)}
                                placeholder="%"
                                disabled={isVersionLocked}
                                data-testid={`input-pml-sourcing-allocation-${row.id}`}
                              />
                            </td>
                            {!isVersionLocked && (
                              <td className="border border-border p-1 text-center">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => deletePmlSourcingRow(row.id)}
                                  className="h-8 w-8"
                                  data-testid={`button-delete-pml-sourcing-${row.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {/* PML Other Costs Section */}
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-foreground" data-testid="text-pml-other-costs-title">{t('pricing.editor.select_potential_other_costs')}</h2>
                    <Button
                      type="button"
                      size="sm"
                      onClick={addPmlOtherCostRow}
                      disabled={isVersionLocked}
                      data-testid="button-add-pml-other-cost-row"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      {t('pricing.editor.add_row')}
                    </Button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse" data-testid="pml-other-costs-table">
                      <thead>
                        <tr className="bg-primary text-primary-foreground">
                          <th className="border border-border p-2 text-left min-w-[200px]">Description</th>
                          <th className="border border-border p-2 text-left min-w-[150px]">Solution</th>
                          <th className="border border-border p-2 text-center min-w-[150px]">{t('pricing.editor.monthly_cost_usd')}</th>
                          <th className="border border-border p-2 w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {otherCostRows.filter(row => row.solution === "Paid Media Lab").length === 0 ? (
                          <tr>
                            <td colSpan={4} className="border border-border p-4 text-center text-muted-foreground">
                              {t('pricing.editor.no_other_costs_added_click_add')}
                            </td>
                          </tr>
                        ) : (
                          otherCostRows.filter(row => row.solution === "Paid Media Lab").map((row) => (
                            <tr key={row.id} data-testid={`pml-other-cost-row-${row.id}`}>
                              <td className="border border-border p-1">
                                <Input
                                  className="h-8 text-sm"
                                  value={row.description}
                                  onChange={(e) => updateOtherCostRow(row.id, 'description', e.target.value)}
                                  placeholder="Description"
                                  disabled={isVersionLocked}
                                  data-testid={`input-pml-other-cost-description-${row.id}`}
                                />
                              </td>
                              <td className="border border-border p-2 text-sm text-muted-foreground">
                                {/* i18n-ok: nombre de solución, vocabulario de la planilla (bifurca lógica) */}
                                Paid Media Lab
                              </td>
                              <td className="border border-border p-1">
                                <Input
                                  type="number"
                                  className="h-8 text-center text-sm"
                                  min={0}
                                  value={row.monthlyCostUSD || ""}
                                  onChange={(e) => {
                                    const val = Math.max(0, parseFloat(e.target.value) || 0);
                                    updateOtherCostRow(row.id, 'monthlyCostUSD', val);
                                  }}
                                  placeholder="0"
                                  disabled={isVersionLocked}
                                  data-testid={`input-pml-other-cost-monthly-${row.id}`}
                                />
                              </td>
                              <td className="border border-border p-1 text-center">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => deleteOtherCostRow(row.id)}
                                  disabled={isVersionLocked}
                                  className="h-8 w-8"
                                  data-testid={`button-delete-pml-other-cost-${row.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            )}

            {activeTab === "team-assets-content" && isDealContextSaved && isContentSelected && (
              <div className="space-y-8" data-testid="team-assets-content-tab">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-8">
                  {/* Left column: Variables + Assets + Team */}
                  <div className="space-y-8">
                    {/* Variables Section */}
                    <Card className="p-6">
                      <h2 className="text-lg font-semibold text-foreground mb-4">Variables</h2>
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <label className="text-sm font-medium text-foreground w-56">{t('pricing.editor.client_geos_pocs')}</label>
                          <Select
                            value={contentVariables.clientGeos}
                            onValueChange={(value) => {
                              setContentVariables(prev => ({ ...prev, clientGeos: value }));
                              setContentTeamRows(prev => prev.map(r =>
                                ["Content Partner", "Content Partner Lead", "Content Specialist Sr", "Content Specialist Lead"].includes(r.role)
                                  ? { ...r, allocationOrPrice: null } : r
                              ));
                              setHasPendingChanges(true);
                            }}
                          >
                            <SelectTrigger className="h-8 w-48" data-testid="select-content-client-geos">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {sheetData?.contentClientGeos?.map((geo) => (
                                <SelectItem key={geo} value={geo}>{geo}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-4">
                          <label className="text-sm font-medium text-foreground w-56">{t('pricing.editor.client_complexity_2')}</label>
                          <Select
                            value={contentVariables.clientComplexity}
                            onValueChange={(value) => {
                              setContentVariables(prev => ({ ...prev, clientComplexity: value }));
                              setContentTeamRows(prev => prev.map(r =>
                                ["Content Partner", "Content Partner Lead", "Content Specialist Sr", "Content Specialist Lead"].includes(r.role)
                                  ? { ...r, allocationOrPrice: null } : r
                              ));
                              setHasPendingChanges(true);
                            }}
                          >
                            <SelectTrigger className="h-8 w-48" data-testid="select-content-client-complexity">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {sheetData?.contentClientComplexity?.map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </Card>

                    {/* Assets Section */}
                    <Card className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-foreground">Assets</h2>
                        <Button
                          type="button"
                          size="sm"
                          onClick={addContentAssetRow}
                          data-testid="button-add-content-asset"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          {t('pricing.editor.add_row')}
                        </Button>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse" data-testid="content-assets-table">
                          <thead>
                            <tr className="bg-primary text-primary-foreground">
                              <th className="border border-border p-2 text-center">{t('pricing.editor.asset_type')}</th>
                              <th className="border border-border p-2 text-center">{t('pricing.editor.assets_q')}</th>
                              <th className="border border-border p-2 text-center"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {contentAssetRows.map((row) => (
                              <tr key={row.id} data-testid={`content-asset-row-${row.id}`}>
                                <td className="border border-border p-1">
                                  <Select
                                    value={row.assetType}
                                    onValueChange={(value) => {
                                      setContentAssetRows(prev => prev.map(r => r.id === row.id ? { ...r, assetType: value } : r));
                                      setContentTeamRows(prev => prev.map(r =>
                                        ["Content Partner", "Content Partner Lead", "Content Specialist Sr", "Content Specialist Lead"].includes(r.role)
                                          ? { ...r, allocationOrPrice: null } : r
                                      ));
                                      setHasPendingChanges(true);
                                    }}
                                  >
                                    <SelectTrigger className="h-8" data-testid={`select-content-asset-type-${row.id}`}>
                                      <SelectValue placeholder="Select" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {sheetData?.contentAssetTypes?.map((type) => (
                                        <SelectItem key={type} value={type}>{type}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="border border-border p-1">
                                  <Input
                                    type="number"
                                    className="h-8 text-center text-sm"
                                    min={0}
                                    value={row.assetQuantity || ""}
                                    onChange={(e) => {
                                      const val = Math.max(0, parseInt(e.target.value) || 0);
                                      setContentAssetRows(prev => prev.map(r => r.id === row.id ? { ...r, assetQuantity: val } : r));
                                      setContentTeamRows(prev => prev.map(r =>
                                        ["Content Partner", "Content Partner Lead", "Content Specialist Sr", "Content Specialist Lead"].includes(r.role)
                                          ? { ...r, allocationOrPrice: null } : r
                                      ));
                                      setHasPendingChanges(true);
                                    }}
                                    placeholder="0"
                                    data-testid={`input-content-asset-qty-${row.id}`}
                                  />
                                </td>
                                <td className="border border-border p-1 text-center">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => deleteContentAssetRow(row.id)}
                                    className="h-8 w-8"
                                    data-testid={`button-delete-content-asset-${row.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 text-destructive" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>

                    {/* Creators & Advocates Section */}
                    {(() => {
                      const creatorsQty = contentAssetRows
                        .filter(r => r.assetType === "Creator Video - Creación")
                        .reduce((s, r) => s + (r.assetQuantity || 0), 0);
                      const advocatesQty = contentAssetRows
                        .filter(r => r.assetType === "Advocates - Creación")
                        .reduce((s, r) => s + (r.assetQuantity || 0), 0);

                      if (creatorsQty === 0 && advocatesQty === 0) return null;

                      const creatorCountries = sheetData?.contentCreatorAdvocateCountries || [];
                      const advocateCountries = sheetData?.contentCreatorAdvocateCountries || [];
                      const allTiers = sheetData?.contentCreatorAdvocateComplexity || [];
                      // Advocates don't have a "0-20" tier in the lookup
                      const advocateTiers = allTiers.filter(tier => tier !== "0-20");

                      const creatorsAllocated = contentCreatorAdvocateDetailData.creators.reduce((s, r) => s + (r.quantity || 0), 0);
                      const advocatesAllocated = contentCreatorAdvocateDetailData.advocates.reduce((s, r) => s + (r.quantity || 0), 0);

                      const addCreatorRow = () => {
                        setContentCreatorAdvocateDetailData(prev => ({
                          ...prev,
                          creators: [...prev.creators, { id: generateId(), country: "", followersTier: "", quantity: 0 }],
                        }));
                        setHasPendingChanges(true);
                      };
                      const deleteCreatorRow = (id: string) => {
                        setContentCreatorAdvocateDetailData(prev => ({
                          ...prev,
                          creators: prev.creators.filter(r => r.id !== id),
                        }));
                        setHasPendingChanges(true);
                      };
                      const updateCreatorRow = (id: string, field: keyof CreatorAdvocateDetailRow, value: string | number) => {
                        setContentCreatorAdvocateDetailData(prev => ({
                          ...prev,
                          creators: prev.creators.map(r => r.id === id ? { ...r, [field]: value } : r),
                        }));
                        setHasPendingChanges(true);
                      };
                      const addAdvocateRow = () => {
                        setContentCreatorAdvocateDetailData(prev => ({
                          ...prev,
                          advocates: [...prev.advocates, { id: generateId(), country: "", followersTier: "", quantity: 0 }],
                        }));
                        setHasPendingChanges(true);
                      };
                      const deleteAdvocateRow = (id: string) => {
                        setContentCreatorAdvocateDetailData(prev => ({
                          ...prev,
                          advocates: prev.advocates.filter(r => r.id !== id),
                        }));
                        setHasPendingChanges(true);
                      };
                      const updateAdvocateRow = (id: string, field: keyof CreatorAdvocateDetailRow, value: string | number) => {
                        setContentCreatorAdvocateDetailData(prev => ({
                          ...prev,
                          advocates: prev.advocates.map(r => r.id === id ? { ...r, [field]: value } : r),
                        }));
                        setHasPendingChanges(true);
                      };

                      const renderDetailTable = (
                        type: "creators" | "advocates",
                        label: string,
                        totalQty: number,
                        allocated: number,
                        rows: CreatorAdvocateDetailRow[],
                        countries: string[],
                        tiers: string[],
                        onAdd: () => void,
                        onDelete: (id: string) => void,
                        onUpdate: (id: string, field: keyof CreatorAdvocateDetailRow, value: string | number) => void
                      ) => (
                        <div className="mb-6">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <h3 className="text-base font-semibold text-yellow-400">{label}</h3>
                              <span className={`text-xs font-medium px-2 py-0.5 rounded ${allocated === totalQty ? 'bg-green-700 text-green-200' : 'bg-yellow-700/60 text-yellow-200'}`}>
                                {allocated} / {totalQty} allocated
                              </span>
                            </div>
                            <Button type="button" size="sm" onClick={onAdd} data-testid={`button-add-${type}-row`}>
                              <Plus className="w-4 h-4 mr-1" /> {t('pricing.editor.add_row')}
                            </Button>
                          </div>
                          {rows.length > 0 ? (
                            <table className="w-full border-collapse" data-testid={`content-${type}-table`}>
                              <thead>
                                <tr className="bg-primary text-primary-foreground">
                                  <th className="border border-border p-2 text-left text-sm">Country</th>
                                  <th className="border border-border p-2 text-left text-sm">{t('pricing.editor.followers_tier')}</th>
                                  <th className="border border-border p-2 text-center text-sm">Quantity</th>
                                  <th className="border border-border p-2 w-10"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {rows.map(row => (
                                  <tr key={row.id} data-testid={`content-${type}-row-${row.id}`}>
                                    <td className="border border-border p-1">
                                      <Select value={row.country} onValueChange={v => onUpdate(row.id, "country", v)}>
                                        <SelectTrigger className="h-8" data-testid={`select-${type}-country-${row.id}`}>
                                          <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                        </SelectContent>
                                      </Select>
                                    </td>
                                    <td className="border border-border p-1">
                                      <Select value={row.followersTier} onValueChange={v => onUpdate(row.id, "followersTier", v)}>
                                        <SelectTrigger className="h-8" data-testid={`select-${type}-tier-${row.id}`}>
                                          <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {tiers.map(tier => <SelectItem key={tier} value={tier}>{t('pricing.editor.k_followers', { n: tier })}</SelectItem>)}
                                        </SelectContent>
                                      </Select>
                                    </td>
                                    <td className="border border-border p-1">
                                      <Input
                                        type="number"
                                        min={0}
                                        value={row.quantity || ""}
                                        placeholder="0"
                                        onChange={e => onUpdate(row.id, "quantity", parseInt(e.target.value) || 0)}
                                        className="h-8 text-center"
                                        data-testid={`input-${type}-qty-${row.id}`}
                                      />
                                    </td>
                                    <td className="border border-border p-1 text-center">
                                      <Button type="button" variant="ghost" size="icon" aria-label={t('common.a11y.delete')} onClick={() => onDelete(row.id)} className="h-8 w-8" data-testid={`button-delete-${type}-${row.id}`}>
                                        <Trash2 className="w-4 h-4 text-destructive" />
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="text-sm text-muted-foreground italic">{t('pricing.editor.no_rows_yet_click_add_row')} {label.toLowerCase()}.</p>
                          )}
                        </div>
                      );

                      return (
                        <Card className="p-6">
                          {/* i18n-ok: vocabulario de la planilla de costos, no se traduce */}
                          <h2 className="text-lg font-semibold text-foreground mb-4">Creators &amp; Advocates</h2>
                          {creatorsQty > 0 && renderDetailTable(
                            "creators", "Creators", creatorsQty, creatorsAllocated,
                            contentCreatorAdvocateDetailData.creators, creatorCountries, allTiers,
                            addCreatorRow, deleteCreatorRow, updateCreatorRow
                          )}
                          {advocatesQty > 0 && renderDetailTable(
                            "advocates", "Advocates", advocatesQty, advocatesAllocated,
                            contentCreatorAdvocateDetailData.advocates, advocateCountries, advocateTiers,
                            addAdvocateRow, deleteAdvocateRow, updateAdvocateRow
                          )}
                        </Card>
                      );
                    })()}

                    {/* Team Section */}
                    <Card className="p-6">
                      <h2 className="text-lg font-semibold text-foreground mb-4">Team</h2>
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse" data-testid="content-team-table">
                          <thead>
                            <tr className="bg-primary text-primary-foreground">
                              <th className="border border-border p-2 text-left">Role</th>
                              <th className="border border-border p-2 text-center">Country</th>
                              <th className="border border-border p-2 text-center">{t('pricing.editor.suggested_team')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {contentTeamRows.filter(row => row.role !== "Creators" && row.role !== "Advocates").map((row) => {
                              const countries = getContentCountriesForRole(row.role);
                              const realIndex = contentTeamRows.findIndex(r => r.role === row.role);
                              return (
                                <tr key={row.role} data-testid={`content-team-row-${row.role}`}>
                                  <td className="border border-border p-2 text-sm font-medium">{row.role}</td>
                                  <td className="border border-border p-1">
                                    <Select
                                      value={row.country}
                                      onValueChange={(value) => updateContentTeamCountry(realIndex, value)}
                                    >
                                      <SelectTrigger className="h-8" data-testid={`select-content-team-country-${row.role}`}>
                                        <SelectValue placeholder="Select" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {countries.map((c) => (
                                          <SelectItem key={c} value={c}>{c}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </td>
                                  <td className="border border-border p-2 text-center">
                                    <span className="text-sm text-muted-foreground bg-muted px-3 py-1 rounded" data-testid={`text-content-allocation-${row.role}`}>
                                      {row.allocationOrPrice !== null ? `${Math.round(row.allocationOrPrice)}%` : '-'}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  </div>

                  {/* Right column: Costs only */}
                  <div className="space-y-8">
                    {/* Costs Section — only populated after Recalculate */}
                    {(() => {
                      const hasCalculated = !hasPendingChanges && solutionCosts["Content"] != null;
                      const totalCost = hasCalculated ? (solutionCosts["Content"] ?? 0) : 0;
                      const creatorsCost = hasCalculated ? contentCreatorAdvocateCostRaw : 0;
                      const teamCost = hasCalculated ? Math.max(0, totalCost - creatorsCost) : 0;
                      const hasAny = hasCalculated && totalCost > 0;
                      const fmt = (n: number) => n > 0 ? `$${Math.round(n).toLocaleString()}` : '-';
                      return (
                        <Card className="p-6">
                          <h2 className="text-lg font-semibold text-foreground mb-4">{t('pricing.editor.costs_months', { count: durationMonths })}</h2>
                          {!hasCalculated && (
                            <p className="text-sm text-muted-foreground italic">{t('pricing.editor.hit_recalculate_to_see_cost_breakdown')}</p>
                          )}
                          {hasCalculated && (
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse" data-testid="content-costs-table">
                              <tbody>
                                <tr>
                                  <td className="p-2 text-sm font-medium">{t('pricing.editor.team_cost')}</td>
                                  <td className="p-2 text-sm text-muted-foreground" data-testid="text-content-team-cost">
                                    {fmt(teamCost)}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="p-2 text-sm font-medium">Creators/Advocate</td>
                                  <td className="p-2 text-sm text-muted-foreground" data-testid="text-content-creator-cost">
                                    {fmt(creatorsCost)}
                                  </td>
                                </tr>
                                <tr className="border-t border-border">
                                  <td className="p-2 text-sm font-bold">{t('pricing.editor.total_deal_cost')}</td>
                                  <td className="p-2 text-sm font-bold" data-testid="text-content-total-cost">
                                    {hasAny ? `$${Math.round(totalCost).toLocaleString()}` : '-'}
                                  </td>
                                </tr>
                                {hasAny && durationMonths > 0 && (
                                  <>
                                    <tr className="border-t border-border bg-muted/30">
                                      <td className="p-2 text-xs text-muted-foreground italic" colSpan={2}>{t('pricing.editor.monthly_breakdown')}</td>
                                    </tr>
                                    <tr>
                                      <td className="p-2 text-xs text-muted-foreground">{t('pricing.editor.team_cost_month')}</td>
                                      <td className="p-2 text-xs text-muted-foreground" data-testid="text-content-team-cost-monthly">
                                        {teamCost > 0 ? `$${Math.round(teamCost / durationMonths).toLocaleString()}` : '-'}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="p-2 text-xs text-muted-foreground">{t('pricing.editor.creators_advocate_month')}</td>
                                      <td className="p-2 text-xs text-muted-foreground" data-testid="text-content-creator-cost-monthly">
                                        {creatorsCost > 0 ? `$${Math.round(creatorsCost / durationMonths).toLocaleString()}` : '-'}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="p-2 text-xs font-semibold text-muted-foreground">{t('pricing.editor.total_month')}</td>
                                      <td className="p-2 text-xs font-semibold text-muted-foreground" data-testid="text-content-total-cost-monthly">
                                        {`$${Math.round(totalCost / durationMonths).toLocaleString()}`}
                                      </td>
                                    </tr>
                                  </>
                                )}
                              </tbody>
                            </table>
                          </div>
                          )}
                        </Card>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "margins" && isDealContextSaved && (
              <div className="p-6 space-y-6 overflow-auto h-full" data-testid="margins-tab">
                {(() => {
                  const allSols = [...teamAssetsSolutions];
                  if (isPmlSelected && !allSols.includes("Paid Media Lab")) allSols.push("Paid Media Lab");
                  if (isContentSelected && !allSols.includes("Content")) allSols.push("Content");
                  const isMulti = allSols.length > 1;
                  const hasMetrics = Object.keys(solutionCosts).length > 0;
                  const dealClient = form.getValues("client") || "—";
                  const dealName = form.getValues("versionName") || "—";
                  const dealCurrency = form.getValues("currency") || "";
                  const dealPayment = form.getValues("paymentTerms") || "—";
                  const fmtUSD = (n: number | null | undefined) =>
                    n != null && n > 0 ? `$${Math.round(n).toLocaleString()}` : "—";
                  const fmtPct = (n: number | null | undefined) =>
                    n != null ? `${Math.round(n)}%` : "—";

                  return (
                    <>
                      {/* Deal header */}
                      <Card className="p-5">
                        <div className="flex flex-wrap gap-x-10 gap-y-2 items-center">
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">Client</p>
                            <p className="text-base font-semibold">{dealClient}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('pricing.editor.deal_name_2')}</p>
                            <p className="text-base font-semibold">{dealName}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">Duration</p>
                            <p className="text-base font-semibold">{durationMonths} month{durationMonths !== 1 ? "s" : ""}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">Currency</p>
                            <p className="text-base font-semibold">{dealCurrency || "—"}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('pricing.editor.payment_terms')}</p>
                            <p className="text-base font-semibold">{dealPayment}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">Solution/s</p>
                            <p className="text-base font-semibold">{allSols.join(", ") || "—"}</p>
                          </div>
                        </div>
                      </Card>

                      {!hasMetrics ? (
                        <Card className="p-8 text-center">
                          <p className="text-muted-foreground text-sm"><Trans i18nKey="pricing.editor.run_recalculate_hint" components={{ b: <span className="font-semibold text-foreground" /> }} /></p>
                        </Card>
                      ) : (
                        <>
                          {/* Per-solution breakdown */}
                          <div>
                            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">{t('pricing.editor.pricing_breakdown_by_solution')}</h2>
                            <div className="overflow-x-auto rounded-xl border border-border">
                              <table className="w-full text-sm" data-testid="margins-summary-table">
                                <thead>
                                  <tr className="bg-muted/60 text-muted-foreground">
                                    <th className="text-left px-4 py-3 font-semibold">Solution</th>
                                    <th className="text-right px-4 py-3 font-semibold">{t('pricing.editor.total_deal_cost')}</th>
                                    <th className="text-right px-4 py-3 font-semibold">{t('pricing.editor.margin')}</th>
                                    <th className="text-right px-4 py-3 font-semibold">{t('pricing.editor.suggested_price')}</th>
                                    <th className="text-right px-4 py-3 font-semibold">{t('pricing.editor.desired_price')}</th>
                                    <th className="text-right px-4 py-3 font-semibold">{t('pricing.editor.obtained_margin')}</th>
                                    <th className="text-right px-4 py-3 font-semibold">Months</th>
                                    <th className="text-right px-4 py-3 font-semibold">{t('pricing.editor.monthly_deal_pricing')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {allSols.map((sol, i) => {
                                    const cost = solutionCosts[sol] ?? 0;
                                    const mEntry = desiredMargins.find(m => m.solution === sol);
                                    const pEntry = desiredPricings.find(p => p.solution === sol);
                                    const margin = mEntry?.margin ?? null;
                                    const suggestedPrice = mEntry?.suggestedPricing ?? null;
                                    const desiredPrice = pEntry?.desiredPricing ?? null;
                                    const obtainedMargin = pEntry?.obtainedMargin ?? null;
                                    const displayPrice = suggestedPrice ?? desiredPrice;
                                    const monthly = displayPrice && durationMonths > 0 ? displayPrice / durationMonths : null;
                                    return (
                                      <tr key={sol} className={i % 2 === 0 ? "bg-background" : "bg-muted/20"} data-testid={`margins-row-${sol}`}>
                                        <td className="px-4 py-3 font-semibold uppercase tracking-wide text-xs">{sol}</td>
                                        <td className="px-4 py-3 text-right text-muted-foreground">{fmtUSD(cost)}</td>
                                        <td className="px-4 py-3 text-right">
                                          {margin != null ? (
                                            <span className="inline-block bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 px-2 py-0.5 rounded font-semibold text-xs">
                                              {fmtPct(margin)}
                                            </span>
                                          ) : "—"}
                                        </td>
                                        <td className="px-4 py-3 text-right font-medium">{fmtUSD(suggestedPrice)}</td>
                                        <td className="px-4 py-3 text-right font-medium">{fmtUSD(desiredPrice)}</td>
                                        <td className="px-4 py-3 text-right text-muted-foreground">{fmtPct(obtainedMargin)}</td>
                                        <td className="px-4 py-3 text-right text-muted-foreground">{durationMonths}</td>
                                        <td className="px-4 py-3 text-right font-semibold text-primary">{fmtUSD(monthly)}</td>
                                      </tr>
                                    );
                                  })}
                                  {isMulti && (
                                    <tr className="border-t-2 border-border bg-muted/40 font-bold" data-testid="margins-row-bundle">
                                      <td className="px-4 py-3 text-xs uppercase tracking-wide">{t('pricing.editor.bundle_total')}</td>
                                      <td className="px-4 py-3 text-right text-muted-foreground">
                                        {fmtUSD(allSols.reduce((s, sol) => s + (solutionCosts[sol] ?? 0), 0))}
                                      </td>
                                      <td className="px-4 py-3 text-right">
                                        {bundleMarginFromSuggested != null ? (
                                          <span className="inline-block bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 px-2 py-0.5 rounded font-semibold text-xs">
                                            {fmtPct(bundleMarginFromSuggested)}
                                          </span>
                                        ) : "—"}
                                      </td>
                                      <td className="px-4 py-3 text-right">{fmtUSD(bundleSuggestedTotal)}</td>
                                      <td className="px-4 py-3 text-right">{fmtUSD(bundleDesiredTotal)}</td>
                                      <td className="px-4 py-3 text-right text-muted-foreground">{fmtPct(bundleObtainedFromDesired)}</td>
                                      <td className="px-4 py-3 text-right text-muted-foreground">{durationMonths}</td>
                                      <td className="px-4 py-3 text-right text-primary">
                                        {fmtUSD(bundleSuggestedTotal && durationMonths > 0 ? bundleSuggestedTotal / durationMonths : null)}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* KPI summary cards */}
                          <div>
                            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">{t('pricing.editor.key_metrics')}</h2>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              {(() => {
                                const totalCost = allSols.reduce((s, sol) => s + (solutionCosts[sol] ?? 0), 0);
                                const totalPrice = isMulti
                                  ? (bundleSuggestedTotal ?? bundleDesiredTotal ?? 0)
                                  : (desiredMargins[0]?.suggestedPricing ?? desiredPricings[0]?.desiredPricing ?? 0);
                                const blendedMargin = totalPrice > 0 ? ((totalPrice - totalCost) / totalPrice) * 100 : null;
                                const monthlyTotal = totalPrice > 0 && durationMonths > 0 ? totalPrice / durationMonths : null;
                                return (
                                  <>
                                    <Card className="p-4 text-center">
                                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('pricing.editor.total_deal_cost')}</p>
                                      <p className="text-xl font-bold">{fmtUSD(totalCost)}</p>
                                    </Card>
                                    <Card className="p-4 text-center">
                                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('pricing.editor.total_deal_pricing')}</p>
                                      <p className="text-xl font-bold text-primary">{fmtUSD(totalPrice)}</p>
                                    </Card>
                                    <Card className="p-4 text-center">
                                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('pricing.editor.blended_deal_margin')}</p>
                                      <p className="text-xl font-bold text-yellow-500">{blendedMargin != null ? fmtPct(blendedMargin) : "—"}</p>
                                    </Card>
                                    <Card className="p-4 text-center">
                                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('pricing.editor.monthly_deal_pricing')}</p>
                                      <p className="text-xl font-bold">{fmtUSD(monthlyTotal)}</p>
                                    </Card>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
